import { useCallback, useEffect, useMemo, useState } from 'react';
import { fmt, fmtFull, dSign, mLbl } from './arrShared';
import { DevOverlay } from '../../components/DevOverlay/DevOverlay';
import {
  KpiCard,
  GaugeChart,
  SectionHeader,
  BarChart,
  VerticalBarChart,
  Treemap,
  SankeyChart,
  WaterfallChart,
  SparkLine,
  MetricCard,
  ArrDetailModal,
} from './arrShared';

const COLORS = ['#1e5fa8', '#15803d', '#6d28d9', '#0891b2', '#d97706', '#dc2626'];
// Matches the reference tool's _TYPE_PALETTE exactly, used only for the ARR
// by LOB cards (assigned by alphabetical index, e.g. Commercial=blue,
// Public Sector=purple) — distinct from the general-purpose COLORS above.
const LOB_PALETTE = ['#1e5fa8', '#6d28d9', '#0891b2', '#059669', '#d97706', '#dc2626'];
// Matches the reference's LOB drill-down bar chart palette exactly (12
// colors, cycling) — used for the "ARR by Customer/Sales Rep/Product"
// bar inside the Drill Down by LOB panel.
const DRILL_PALETTE = ['#1e5fa8', '#15803d', '#d97706', '#6d28d9', '#0f766e', '#b91c1c', '#ea580c', '#3b82c4', '#22c55e', '#a78bfa', '#fb923c', '#64748b'];

const getPrevMonth = (month) => {
  if (!month) return '';
  const date = new Date(Number(month.slice(0, 4)), Number(month.slice(5, 7)) - 2, 1);
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
};

const shiftMonth = (month, offset) => {
  if (!month) return '';
  const date = new Date(Number(month.slice(0, 4)), Number(month.slice(5, 7)) - 1 + offset, 1);
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
};

const pct = (value, total) => (Number(total) > 0 ? ((Number(value || 0) / Number(total)) * 100).toFixed(1) : '0.0');
const moneyDash = (v) => (Math.abs(Number(v || 0)) > 0 ? fmtFull(v) : '-');
// Matches the reference's exact MoM cell format in drill-down tables:
// arrow prefix (only when nonzero) + FULL-precision abs value — unlike
// dSign, which uses arrows with compact (K/M) notation for other cards.
const arrowFull = (v) => {
  const n = Number(v || 0);
  if (n > 0) return `▲ ${fmtFull(n)}`;
  if (n < 0) return `▼ ${fmtFull(Math.abs(n))}`;
  return '—';
};
// Reference's Customer ARR Detail expand-row uses a THIRD MoM format, distinct
// from both dSign (arrow+compact) and arrowFull (arrow+full): a plain "+ "/"- "
// text sign (not an arrow glyph) plus full-precision value.
const signFull = (v) => {
  const n = Number(v || 0);
  if (n > 0) return `+ ${fmtFull(n)}`;
  if (n < 0) return `- ${fmtFull(Math.abs(n))}`;
  return '—';
};
// LOB drill-down row-detail "MoM" hero stat (reference's ms2 helper) always
// shows an arrow — unlike arrowFull, it has no zero/em-dash branch: `mp` is
// '▲ ' whenever mom>=0 (including exactly 0), so a flat month shows "▲ $0".
const heroArrow = (v) => {
  const n = Number(v || 0);
  return (n >= 0 ? '▲ ' : '▼ ') + fmtFull(Math.abs(n));
};
const fmtModalMoney = (v) =>
  new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  }).format(Number(v || 0));
const signedModalMoney = (v) => {
  const n = Number(v || 0);
  if (n > 0) return `+${fmtModalMoney(n)}`;
  if (n < 0) return `−${fmtModalMoney(Math.abs(n))}`;
  return fmtModalMoney(0);
};

// Exact port of the reference tool's fS() — used for chart value labels that
// must match it precisely (e.g. ARR by Sales Rep) rather than our own fmt(),
// whose compact-notation rounding/decimal rules diverge from it at the edges
// (e.g. whether a trailing ".0"/".00" is kept, K vs M cutover for mid-range
// values).
const fmtHtmlCompact = (v) => {
  const n = Number(v || 0);
  if (!n || Number.isNaN(n)) return '$0';
  const a = Math.abs(n);
  const s = n < 0 ? '-' : '';
  if (a >= 1e6) return `${s}$${(a / 1e6).toFixed(2)}M`;
  if (a >= 1e3) return `${s}$${(a / 1e3).toFixed(1)}K`;
  return `${s}$${Math.round(a).toLocaleString()}`;
};

// Reference tool's ARR-by-Sales-Rep "Closing ARR" mode overrides its tooltip
// to always show millions (2 decimals), even for reps under $1M — e.g. a
// $50,000 rep shows "$0.05M", not "$50K". "New Wins" mode uses fS() as-is.
const fmtHtmlMillions = (v) => {
  const n = Number(v || 0);
  const s = n < 0 ? '-' : '';
  return `${s}$${(Math.abs(n) / 1e6).toFixed(2)}M`;
};

function isNonEmptyChange(changes) {
  return !!changes && Object.values(changes).some((value) => Number(value || 0) !== 0);
}

function changeForDeal(deal, month) {
  const raw = deal.changes?.[month] || {};
  const normalized = {};
  Object.entries(raw).forEach(([k, v]) => {
    const key = k.trim().toLowerCase();
    normalized[key] = (normalized[key] || 0) + Number(v || 0);
  });
  return normalized;
}

function toDeal(row) {
  return {
    key_id: row.key_id,
    end_user: row.end_user || 'Unspecified',
    region: row.business_unit || 'Unspecified',
    sales_person: row.sales_person || 'Unassigned',
    sub_product: row.product_l2 || row.product_type || '',
    product_category: row.product_l1 || '',
    booking_type: row.line_of_business || row.deal_type || 'Commercial',
    mode: row.mode || '',
    contract_id: row.contract_id || '',
    contract_name: row.contract_name || '',
    order_status: row.order_status || row.booking_status || '',
    rec_non_rec: row.revenue_type || '',
    term_start: row.term_start || '',
    term_end: row.term_end || '',
    arr: row.monthly_arr || {},
    changes: row.monthly_changes || {},
  };
}

function buildReferenceData(records) {
  const deals = records.map(toDeal);
  const monthlyArr = {};
  const monthlyChanges = {};
  const monthSet = new Set();
  deals.forEach((deal) => {
    Object.entries(deal.arr || {}).forEach(([month, value]) => {
      monthSet.add(month);
      monthlyArr[month] = (monthlyArr[month] || 0) + Number(value || 0);
    });
    Object.entries(deal.changes || {}).forEach(([month, changes]) => {
      if (!isNonEmptyChange(changes)) return;
      monthSet.add(month);
      monthlyChanges[month] = monthlyChanges[month] || {};
      Object.entries(changes || {}).forEach(([type, amount]) => {
        monthlyChanges[month][type] = (monthlyChanges[month][type] || 0) + Number(amount || 0);
      });
    });
  });

  const allMonths = [...monthSet].sort();
  const latestFromArr = [...allMonths].reverse().find((m) => Number(monthlyArr[m] || 0) > 0) || '';
  const latestFromChanges = Object.keys(monthlyChanges).sort().at(-1) || '';
  const latestMonth = (latestFromChanges > latestFromArr ? latestFromChanges : latestFromArr) || allMonths.at(-1) || '';
  return { deals, monthlyArr, monthlyChanges, allMonths, latestMonth };
}

function computeFiltered(deals, allMonths, latestMonth, from, to) {
  const arr = {};
  const chg = {};
  allMonths.forEach((month) => {
    if (latestMonth && month > latestMonth) return;
    if (from && month < from) return;
    if (to && month > to) return;
    let monthArr = 0;
    const monthChg = {};
    deals.forEach((deal) => {
      if (deal.arr?.[month]) monthArr += Number(deal.arr[month] || 0);
      const changes = changeForDeal(deal, month);
      if (isNonEmptyChange(changes)) {
        Object.entries(changes).forEach(([type, amount]) => {
          monthChg[type] = (monthChg[type] || 0) + Number(amount || 0);
        });
      }
    });
    arr[month] = monthArr;
    if (Object.keys(monthChg).length) chg[month] = monthChg;
  });
  return { arr, chg };
}

function buildReferenceView(records, periodInfo = {}) {
  const ref = buildReferenceData(records);
  const { deals, allMonths, latestMonth, monthlyArr } = ref;
  const from = periodInfo.from || (latestMonth ? `${latestMonth.slice(0, 4)}-01` : '');
  const to = periodInfo.to || latestMonth;
  const comp = computeFiltered(deals, allMonths, latestMonth, from, to);
  const months = Object.keys(comp.arr).filter((m) => !latestMonth || m <= latestMonth).sort();
  const latest = months.at(-1) || latestMonth;
  const prev = getPrevMonth(latest);
  const curr = comp.arr[latest] || 0;
  const prv = comp.arr[prev] || 0;
  const allRangeMonths = allMonths.filter((m) => (!from || m >= from) && (!to || m <= to));

  let pNew = 0;
  let pUp = 0;
  let pRen = 0;
  let pChurn = 0;
  let pDn = 0;
  allRangeMonths.forEach((month) => {
    const c = comp.chg[month];
    if (!c) return;
    pNew += Math.max(0, Number(c.new || 0));
    pUp += Math.max(0, Number(c.upsell || 0));
    pRen += Number(c.renewal || 0);
    pChurn += Math.min(0, Number(c.churn || 0));
    pDn += Math.abs(Number(c.downsell || 0));
  });

  const sankeyPrevStart = from ? getPrevMonth(from) : '';
  const sankeyOpeningARR = deals.reduce((sum, deal) => (
    sum + Number(deal.arr?.[sankeyPrevStart] || deal.arr?.[from] || 0)
  ), 0);
  let sankeyNew = 0;
  let sankeyUp = 0;
  let sankeyRen = 0;
  let sankeyChurn = 0;
  let sankeyDn = 0;
  deals.forEach((deal) => {
    allRangeMonths.forEach((month) => {
      const c = changeForDeal(deal, month);
      if (!isNonEmptyChange(c)) return;
      sankeyNew += Math.abs(Number(c.new || 0));
      sankeyUp += Math.abs(Number(c.upsell || 0));
      sankeyRen += Math.abs(Number(c.renewal || 0));
      sankeyChurn += Math.abs(Number(c.churn || 0));
      sankeyDn += Math.abs(Number(c.downsell || 0));
    });
  });

  const priorYearDec = latest ? `${Number(latest.slice(0, 4)) - 1}-12` : '';
  const baseFromFiltered = deals.reduce((sum, deal) => sum + Number(deal.arr?.[priorYearDec] || 0), 0);
  const periodStartARR = comp.arr[priorYearDec] || baseFromFiltered || monthlyArr[priorYearDec] || comp.arr[months[0]] || 0;
  const nrrBase = periodStartARR;
  const periodChange = curr - periodStartARR;
  const periodChangePct = periodStartARR > 0 ? (periodChange / periodStartARR) * 100 : 0;
  const grr = nrrBase > 0 ? ((nrrBase + pChurn - pDn) / nrrBase) * 100 : 0;
  const nrr = nrrBase > 0 ? ((nrrBase + pChurn - pDn + pUp) / nrrBase) * 100 : 0;

  const ltmStart = shiftMonth(latest, -11);
  const ltmPrior = shiftMonth(ltmStart, -1);
  const ltmMonths = allMonths.filter((m) => (!ltmStart || m >= ltmStart) && (!latest || m <= latest));
  const ltmComp = computeFiltered(deals, allMonths, latestMonth, ltmStart, latest);
  const ltmPriorARR = deals.reduce((sum, deal) => sum + Number(deal.arr?.[ltmPrior] || 0), 0);
  let ltmNew = 0;
  let ltmUp = 0;
  let ltmChurn = 0;
  let ltmDn = 0;
  ltmMonths.forEach((month) => {
    const c = ltmComp.chg[month];
    if (!c) return;
    ltmNew += Math.max(0, Number(c.new || 0));
    ltmUp += Math.max(0, Number(c.upsell || 0));
    ltmChurn += Math.min(0, Number(c.churn || 0));
    ltmDn += Math.abs(Number(c.downsell || 0));
  });
  const ltmGrr = ltmPriorARR > 0 ? ((ltmPriorARR + ltmChurn - ltmDn) / ltmPriorARR) * 100 : grr;
  const ltmNrr = ltmPriorARR > 0 ? ((ltmPriorARR + ltmChurn - ltmDn + ltmUp) / ltmPriorARR) * 100 : nrr;

  const trend = months
    .filter((month) => !latestMonth || (month <= latestMonth && Number(comp.arr[month] || 0) > 0))
    .map((month) => ({ month, value: Number(comp.arr[month] || 0) }));
  const playTrend = allMonths
    .filter((month) => !latestMonth || (month <= latestMonth && Number(monthlyArr[month] || 0) > 0))
    .map((month) => ({ month, value: Number(monthlyArr[month] || 0) }));

  const waterfall = allRangeMonths.slice(-18).map((month) => {
    const c = comp.chg[month] || {};
    return {
      month,
      new: Number(c.new || 0),
      upsell: Number(c.upsell || 0),
      renewal: Number(c.renewal || 0),
      churn: Number(c.churn || 0),
      downsell: Number(c.downsell || 0),
    };
  });

  return {
    ...ref,
    from,
    to,
    months,
    latest,
    prev,
    curr,
    prv,
    pNew,
    pUp,
    pRen,
    pChurn,
    pDn,
    nrrBase,
    periodStartARR,
    periodChange,
    periodChangePct,
    grr,
    nrr,
    ltmMonths,
    ltmPriorARR,
    ltmNew,
    ltmUp,
    ltmChurn,
    ltmDn,
    ltmGrr,
    ltmNrr,
    sankeyOpeningARR,
    sankeyNew,
    sankeyUp,
    sankeyRen,
    sankeyChurn,
    sankeyDn,
    trend,
    playTrend,
    waterfall,
    allRangeMonths,
  };
}

function groupCurrent(deals, latest, field) {
  const groups = {};
  deals.forEach((deal) => {
    const label = deal[field] || 'Unspecified';
    groups[label] = groups[label] || { label, value: 0, contracts: 0 };
    groups[label].value += Number(deal.arr?.[latest] || 0);
    groups[label].contracts += 1;
  });
  return Object.values(groups).sort((a, b) => b.value - a.value);
}

function groupMetrics(deals, latest, prev, months, field) {
  const groups = {};
  deals.forEach((deal) => {
    const label = deal[field] || 'Unspecified';
    groups[label] = groups[label] || { label, value: 0, prev: 0, newWon: 0, churn: 0, contracts: 0 };
    const group = groups[label];
    group.value += Number(deal.arr?.[latest] || 0);
    group.prev += Number(deal.arr?.[prev] || 0);
    group.contracts += 1;
    months.forEach((month) => {
      const c = changeForDeal(deal, month);
      if (!isNonEmptyChange(c)) return;
      group.newWon += Math.max(0, Number(c.new || 0)) + Math.max(0, Number(c.upsell || 0));
      group.churn += Number(c.churn || 0) + Number(c.downsell || 0);
    });
  });
  return Object.values(groups)
    .map((group) => {
      const lost = Math.abs(Number(group.churn || 0));
      const nrr = group.prev > 0 ? ((group.prev - lost + group.newWon) / group.prev) * 100 : 0;
      return { ...group, mom: group.value - group.prev, nrr };
    })
    .sort((a, b) => b.value - a.value);
}

function groupProducts(deals, latest, months, field = 'sub_product') {
  const groups = {};
  deals.forEach((deal) => {
    const label = deal[field] || 'Unspecified';
    groups[label] = groups[label] || { label, value: 0, newWon: 0, churn: 0, contracts: 0 };
    groups[label].value += Number(deal.arr?.[latest] || 0);
    groups[label].contracts += 1;
    months.forEach((month) => {
      const c = changeForDeal(deal, month);
      if (!isNonEmptyChange(c)) return;
      groups[label].newWon += Math.max(0, Number(c.new || 0)) + Math.max(0, Number(c.upsell || 0));
      groups[label].churn += Number(c.churn || 0) + Number(c.downsell || 0);
    });
  });
  return Object.values(groups).sort((a, b) => b.value - a.value);
}

function trendByField(deals, months, field, mode = 'arr') {
  const labels = [...new Set(deals.map((deal) => deal[field] || 'Unspecified'))];
  return labels.map((label) => ({
    label,
    data: months.map((month) => {
      let value = 0;
      deals.forEach((deal) => {
        if ((deal[field] || 'Unspecified') !== label) return;
        if (mode === 'arr') {
          value += Number(deal.arr?.[month] || 0);
        } else {
          const c = changeForDeal(deal, month);
          value += Math.max(0, Number(c.new || 0)) + Math.max(0, Number(c.upsell || 0));
        }
      });
      return { month, value };
    }),
  })).filter((series) => series.data.some((point) => Number(point.value || 0) !== 0));
}

function buildCustomerRows(deals, latest, prev, months) {
  const rows = {};
  deals.forEach((deal) => {
    const key = `${deal.end_user}||${deal.sub_product}`;
    rows[key] = rows[key] || {
      customer: deal.end_user,
      region: deal.region,
      sub_product: deal.sub_product || '-',
      sales_person: deal.sales_person,
      curr_arr: 0,
      prev_arr: 0,
      new_won: 0,
      period_churn: 0,
      trend: months.map((month) => ({ month, value: 0 })),
    };
    const row = rows[key];
    row.curr_arr += Number(deal.arr?.[latest] || 0);
    row.prev_arr += Number(deal.arr?.[prev] || 0);
    row.trend.forEach((point) => {
      point.value += Number(deal.arr?.[point.month] || 0);
    });
    months.forEach((month) => {
      const c = changeForDeal(deal, month);
      if (!isNonEmptyChange(c)) return;
      if (Number(c.new || 0) > 0) row.new_won += Number(c.new || 0);
      if (Number(c.upsell || 0) > 0) row.new_won += Number(c.upsell || 0);
      if (c.churn) row.period_churn += Number(c.churn || 0);
      row.period_churn += Number(c.downsell || 0);
    });
  });
  return Object.values(rows)
    .map((row) => ({ ...row, mom: row.curr_arr - row.prev_arr }))
    .sort((a, b) => b.curr_arr - a.curr_arr);
}

function TrendPlaybackChart({ data = [] }) {
  const [idx, setIdx] = useState(Math.max(0, data.length - 1));
  const [playing, setPlaying] = useState(false);
  const [speed, setSpeed] = useState(450);

  useEffect(() => {
    setIdx(Math.max(0, data.length - 1));
    setPlaying(false);
  }, [data]);

  useEffect(() => {
    if (!playing || data.length < 2) return undefined;
    const timer = setInterval(() => {
      setIdx((current) => {
        if (current >= data.length - 1) {
          setPlaying(false);
          return current;
        }
        return current + 1;
      });
    }, speed);
    return () => clearInterval(timer);
  }, [playing, speed, data.length]);

  const visible = data.slice(0, Math.min(idx + 1, data.length));
  const month = data[idx]?.month || '-';

  const stop = () => {
    setPlaying(false);
    setIdx(Math.max(0, data.length - 1));
  };
  const step = (dir) => {
    setPlaying(false);
    setIdx((current) => Math.max(0, Math.min(data.length - 1, current + dir)));
  };
  const play = () => {
    if (playing) {
      setPlaying(false);
      return;
    }
    setIdx((current) => (current >= data.length - 1 ? 0 : current));
    setPlaying(true);
  };

  // Global keyboard shortcuts, matching the reference tool: Space toggles
  // play/pause, ←/→ step a month, R stops (jumps to the latest month).
  // Ignored while typing in an input/select/textarea, same as the reference.
  useEffect(() => {
    const onKeyDown = (event) => {
      const tag = event.target?.tagName;
      if (tag === 'INPUT' || tag === 'SELECT' || tag === 'TEXTAREA') return;
      if (event.code === 'Space') {
        event.preventDefault();
        play();
      } else if (event.code === 'ArrowRight') {
        step(1);
      } else if (event.code === 'ArrowLeft') {
        step(-1);
      } else if (event.code === 'KeyR' && !event.ctrlKey) {
        stop();
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  });

  return (
    <>
      <div className="arr-play-bar">
        <button type="button" className={`arr-play-btn play ${playing ? 'pause' : ''}`} onClick={play}>{playing ? '▮▮' : '▶'}</button>
        <button type="button" className="arr-play-btn stop" onClick={stop}>■</button>
        <button type="button" className="arr-play-btn step" onClick={() => step(-1)}>◀</button>
        <button type="button" className="arr-play-btn step" onClick={() => step(1)}>▶▶</button>
        <div className="arr-play-range">
          <span className="arr-play-label">MONTH</span>
          <input
            type="range"
            className="arr-play-slider"
            min="0"
            max={Math.max(0, data.length - 1)}
            value={idx}
            onChange={(event) => {
              setPlaying(false);
              setIdx(Number(event.target.value));
            }}
          />
          <span className="arr-play-month">{month}</span>
        </div>
        <span className="arr-play-label">SPEED</span>
        <select className="arr-play-speed" value={speed} onChange={(event) => setSpeed(Number(event.target.value))}>
          <option value="800">0.5x</option>
          <option value="450">1x</option>
          <option value="200">2x</option>
          <option value="80">5x</option>
        </select>
      </div>
      <TrendSvg data={visible} />
    </>
  );
}

function TrendSvg({ data = [] }) {
  const [hoverIdx, setHoverIdx] = useState(null);
  const W = 900;
  const H = 290;
  const pad = { l: 72, r: 26, t: 24, b: 42 };
  if (data.length < 2) {
    return <div className="arr-trend-canvas" />;
  }
  const vals = data.map((d) => Number(d.value || 0));
  const minV = Math.min(...vals, 0);
  const maxV = Math.max(...vals, 1);
  const range = maxV - minV || 1;
  const plotW = W - pad.l - pad.r;
  const plotH = H - pad.t - pad.b;
  const x = (i) => pad.l + (i / Math.max(1, data.length - 1)) * plotW;
  const y = (v) => pad.t + plotH - ((Number(v || 0) - minV) / range) * plotH;
  const pts = data.map((d, i) => ({ x: x(i), y: y(d.value), ...d }));
  const line = pts.map((p) => `${p.x.toFixed(1)},${p.y.toFixed(1)}`).join(' ');
  const area = `${pad.l},${pad.t + plotH} ${line} ${pts.at(-1).x.toFixed(1)},${pad.t + plotH}`;
  const ticks = [0, 0.25, 0.5, 0.75, 1].map((t) => {
    const value = minV + range * t;
    return { value, y: y(value) };
  });
  const xTicks = data.filter((_, i) => i === 0 || i === data.length - 1 || i % Math.ceil(data.length / 10) === 0);
  const hoverPoint = hoverIdx !== null ? pts[hoverIdx] : pts.at(-1);
  const tooltipW = 152;
  const tooltipH = 76;
  const tooltipX = Math.min(Math.max((hoverPoint?.x || 0) + 12, pad.l), W - tooltipW - 12);
  const tooltipY = Math.min(Math.max((hoverPoint?.y || 0) - 52, pad.t + 4), H - tooltipH - pad.b);

  return (
    <svg className="arr-trend-canvas" viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" onMouseLeave={() => setHoverIdx(null)}>
      <defs>
        <linearGradient id="arrTrendFill" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#1e5fa8" stopOpacity="0.18" />
          <stop offset="100%" stopColor="#1e5fa8" stopOpacity="0.02" />
        </linearGradient>
      </defs>
      {ticks.map((tick) => (
        <g key={tick.value}>
          <line x1={pad.l} x2={W - pad.r} y1={tick.y} y2={tick.y} stroke="rgba(0,0,0,.05)" />
          <text x={pad.l - 10} y={tick.y + 4} textAnchor="end" fontSize="10" fill="#8898aa">{fmt(tick.value)}</text>
        </g>
      ))}
      <line x1={pad.l} x2={pad.l} y1={pad.t} y2={pad.t + plotH} stroke="rgba(0,0,0,.08)" />
      <line x1={pad.l} x2={W - pad.r} y1={pad.t + plotH} y2={pad.t + plotH} stroke="rgba(0,0,0,.08)" />
      <polygon points={area} fill="url(#arrTrendFill)" />
      <polyline points={line} fill="none" stroke="#1e5fa8" strokeWidth="2.3" vectorEffect="non-scaling-stroke" />
      {pts.map((point, i) => (
        <g key={point.month}>
          <circle cx={point.x} cy={point.y} r={i === pts.length - 1 || i === hoverIdx ? 4.8 : 2.3} fill={i === pts.length - 1 || i === hoverIdx ? '#1e5fa8' : 'rgba(30,95,168,.45)'} />
          <circle
            cx={point.x}
            cy={point.y}
            r="12"
            fill="transparent"
            onMouseEnter={() => setHoverIdx(i)}
            onMouseMove={() => setHoverIdx(i)}
          />
        </g>
      ))}
      {xTicks.map((tick) => (
        <text key={tick.month} x={x(data.indexOf(tick))} y={H - 14} textAnchor="middle" fontSize="9" fill="#8898aa">{mLbl(tick.month)}</text>
      ))}
      {hoverPoint && (
        <g className="arr-trend-tooltip" pointerEvents="none">
          <rect x={tooltipX} y={tooltipY} width={tooltipW} height={tooltipH} rx="8" fill="rgba(31,31,31,.92)" />
          <rect x={tooltipX} y={tooltipY} width={tooltipW} height={tooltipH} rx="8" fill="none" stroke="rgba(255,255,255,.08)" />
          <text x={tooltipX + 12} y={tooltipY + 24} fill="#fff" fontSize="17" fontWeight="800" fontFamily="Inter, system-ui, sans-serif">
            {hoverPoint.month}
          </text>
          <rect x={tooltipX + 12} y={tooltipY + 39} width="18" height="18" fill="rgba(59,130,246,.18)" stroke="#3b82c4" strokeWidth="3" />
          <text x={tooltipX + 38} y={tooltipY + 54} fill="#fff" fontSize="16" fontWeight="800" fontFamily="Inter, system-ui, sans-serif">
            ARR: {fmt(hoverPoint.value)}
          </text>
        </g>
      )}
    </svg>
  );
}

function ChartToggle({ options, value, onChange }) {
  return (
    <div className="arr-chart-toggle">
      {options.map((option) => (
        <button
          key={option.value}
          type="button"
          className={`arr-ctbtn ${value === option.value ? 'on' : ''}`}
          onClick={() => onChange(option.value)}
        >
          {option.label}
        </button>
      ))}
    </div>
  );
}

function polarPoint(cx, cy, r, angleDeg) {
  const rad = ((angleDeg - 90) * Math.PI) / 180;
  return { x: cx + r * Math.cos(rad), y: cy + r * Math.sin(rad) };
}

// Donut-wedge path: angleDeg 0 = 12 o'clock, increasing clockwise.
function donutSlicePath(cx, cy, rOuter, rInner, startAngle, endAngle) {
  const largeArc = endAngle - startAngle > 180 ? 1 : 0;
  const outerStart = polarPoint(cx, cy, rOuter, startAngle);
  const outerEnd = polarPoint(cx, cy, rOuter, endAngle);
  const innerEnd = polarPoint(cx, cy, rInner, endAngle);
  const innerStart = polarPoint(cx, cy, rInner, startAngle);
  return [
    `M ${outerStart.x} ${outerStart.y}`,
    `A ${rOuter} ${rOuter} 0 ${largeArc} 1 ${outerEnd.x} ${outerEnd.y}`,
    `L ${innerEnd.x} ${innerEnd.y}`,
    `A ${rInner} ${rInner} 0 ${largeArc} 0 ${innerStart.x} ${innerStart.y}`,
    'Z',
  ].join(' ');
}

function BUChart({ items = [], mode = 'arr' }) {
  const [hoverIdx, setHoverIdx] = useState(null);
  const colors = ['#1e5fa8', '#15803d', '#7c3aed', '#0891b2', '#d97706'];
  const rows = items
    .filter((item) => Number(item.value || 0) !== 0)
    .sort((a, b) => Number(b.value || 0) - Number(a.value || 0));
  const total = rows.reduce((sum, item) => sum + Number(item.value || 0), 0);

  if (!rows.length) {
    return <div className="arr-bu-empty">No BU data</div>;
  }

  if (mode === 'new') {
    const max = Math.max(...rows.map((row) => Number(row.value || 0)), 1);
    const hovered = hoverIdx != null ? rows[hoverIdx] : null;
    return (
      <div className="arr-bu-bar-wrap" onMouseLeave={() => setHoverIdx(null)}>
        <div className="arr-bu-yaxis">
          {[1, 0.8, 0.6, 0.4, 0.2, 0].map((tick) => (
            <span key={tick}>{fmt(max * tick)}</span>
          ))}
        </div>
        <div className="arr-bu-bars">
          {rows.map((row, i) => {
            const height = Math.max(3, (Number(row.value || 0) / max) * 100);
            return (
              <div className="arr-bu-bar-col" key={row.label}>
                <div className="arr-bu-bar-track">
                  <button
                    type="button"
                    className="arr-bu-bar"
                    style={{ height: `${height}%`, background: colors[i % colors.length] }}
                    onMouseEnter={() => setHoverIdx(i)}
                    onFocus={() => setHoverIdx(i)}
                    aria-label={`${row.label} ${fmt(row.value)}`}
                  />
                  {hovered?.label === row.label && (
                    <div className="arr-bu-tip arr-bu-tip-bar">
                      <strong>{row.label}</strong>
                      <span><i style={{ background: colors[i % colors.length] }} />{fmt(row.value)}</span>
                    </div>
                  )}
                </div>
                <div className="arr-bu-xlabel">{row.label}</div>
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  // Segment angles (in degrees, 0=top, clockwise) drive the actual rendered
  // wedge shapes below — the hover target IS the wedge itself, so there is no
  // separate hotspot geometry that can ever drift out of alignment with it.
  let cumAngle = 0;
  const segments = rows.map((row, i) => {
    const pctVal = total > 0 ? (Number(row.value || 0) / total) * 100 : 0;
    const startAngle = cumAngle;
    cumAngle += (pctVal / 100) * 360;
    return { row, i, startAngle, endAngle: cumAngle, midAngle: (startAngle + cumAngle) / 2, pctVal };
  });

  const CX = 100, CY = 100, R_OUTER = 82, R_INNER = 50, POP = 6;
  const active = hoverIdx != null ? segments[hoverIdx] : null;

  return (
    <div className="arr-bu-donut-wrap" onMouseLeave={() => setHoverIdx(null)}>
      <div className="arr-bu-donut-stage">
      <svg viewBox="0 0 200 200" className="arr-bu-donut-svg">
        {segments.map((seg) => {
          const isActive = hoverIdx === seg.i;
          const midRad = ((seg.midAngle - 90) * Math.PI) / 180;
          const dx = isActive ? Math.cos(midRad) * POP : 0;
          const dy = isActive ? Math.sin(midRad) * POP : 0;
          return (
            <path
              key={seg.row.label}
              d={donutSlicePath(CX, CY, R_OUTER, R_INNER, seg.startAngle, seg.endAngle)}
              className={`arr-bu-donut-slice${isActive ? ' active' : ''}`}
              fill={colors[seg.i % colors.length]}
              style={{
                transform: `translate(${dx}px, ${dy}px)`,
                opacity: hoverIdx == null || isActive ? 1 : 0.55,
              }}
              tabIndex={0}
              role="button"
              onMouseEnter={() => setHoverIdx(seg.i)}
              onFocus={() => setHoverIdx(seg.i)}
              aria-label={`${seg.row.label} ${fmt(seg.row.value)}`}
            >
              <title>{`${seg.row.label}: ${fmt(seg.row.value)} (${seg.pctVal.toFixed(1)}%)`}</title>
            </path>
          );
        })}
      </svg>
      <div className="arr-bu-donut-center">
        {active ? (
          <>
            <div className="arr-bu-donut-center-label">{active.row.label}</div>
            <div className="arr-bu-donut-center-val" style={{ color: colors[active.i % colors.length] }}>{fmt(active.row.value)}</div>
            <div className="arr-bu-donut-center-pct">{active.pctVal.toFixed(1)}% of total</div>
          </>
        ) : (
          <>
            <div className="arr-bu-donut-center-label">Total</div>
            <div className="arr-bu-donut-center-val">{fmt(total)}</div>
          </>
        )}
      </div>
      </div>
      <div className="arr-bu-legend">
        {segments.map((seg) => (
          <button
            key={seg.row.label}
            type="button"
            className={hoverIdx === seg.i ? 'active' : ''}
            onMouseEnter={() => setHoverIdx(seg.i)}
            onFocus={() => setHoverIdx(seg.i)}
          >
            <i style={{ background: colors[seg.i % colors.length] }} />{seg.row.label}
          </button>
        ))}
      </div>
    </div>
  );
}

// Matches the reference tool's SUB_COLS exactly (6 colors, cycling).
const SUB_PRODUCT_COLORS = ['#1e5fa8', '#15803d', '#d97706', '#6d28d9', '#0f766e', '#b91c1c'];

function SubProductDonut({ items = [] }) {
  const [hoverIdx, setHoverIdx] = useState(null);
  const rows = items
    .filter((item) => Number(item.value || 0) > 0)
    .sort((a, b) => Number(b.value || 0) - Number(a.value || 0));
  const total = rows.reduce((sum, item) => sum + Number(item.value || 0), 0);

  if (!rows.length) return <div className="arr-bu-empty">No sub-product data</div>;

  // Same SVG-wedge approach as BUChart: the hover target IS the rendered
  // slice, so it can never drift out of alignment the way the old
  // conic-gradient + rotated-hotspot technique did.
  let cumAngle = 0;
  const segments = rows.map((row, i) => {
    const pctVal = total > 0 ? (Number(row.value || 0) / total) * 100 : 0;
    const startAngle = cumAngle;
    cumAngle += (pctVal / 100) * 360;
    return { row, i, startAngle, endAngle: cumAngle, midAngle: (startAngle + cumAngle) / 2, pctVal };
  });

  const CX = 100, CY = 100, R_OUTER = 82, R_INNER = 50, POP = 8;
  const active = hoverIdx != null ? segments[hoverIdx] : null;

  return (
    <div className="arr-prod-donut-layout" onMouseLeave={() => setHoverIdx(null)}>
      <div className="arr-bu-donut-stage">
        <svg viewBox="0 0 200 200" className="arr-bu-donut-svg">
          {segments.map((seg) => {
            const isActive = hoverIdx === seg.i;
            const midRad = ((seg.midAngle - 90) * Math.PI) / 180;
            const dx = isActive ? Math.cos(midRad) * POP : 0;
            const dy = isActive ? Math.sin(midRad) * POP : 0;
            return (
              <path
                key={seg.row.label}
                d={donutSlicePath(CX, CY, R_OUTER, R_INNER, seg.startAngle, seg.endAngle)}
                className={`arr-bu-donut-slice${isActive ? ' active' : ''}`}
                fill={SUB_PRODUCT_COLORS[seg.i % SUB_PRODUCT_COLORS.length]}
                style={{
                  // Reference draws each doughnut wedge's border in the SAME
                  // color as its fill (borderColor:SUB_COLS), not a fixed
                  // white separator — with a tiny sub-1% slice (e.g. "1Key"
                  // at ~0.2%/0.7deg), the shared .arr-bu-donut-slice white
                  // stroke is wider than the fill itself and erases it
                  // entirely. Match the reference so slim slices stay visible.
                  stroke: SUB_PRODUCT_COLORS[seg.i % SUB_PRODUCT_COLORS.length],
                  transform: `translate(${dx}px, ${dy}px)`,
                  opacity: hoverIdx == null || isActive ? 1 : 0.55,
                }}
                tabIndex={0}
                role="button"
                onMouseEnter={() => setHoverIdx(seg.i)}
                onFocus={() => setHoverIdx(seg.i)}
                aria-label={`${seg.row.label} ${fmt(seg.row.value)}`}
              >
                <title>{`${seg.row.label}: ${fmt(seg.row.value)} (${seg.pctVal.toFixed(1)}%)`}</title>
              </path>
            );
          })}
        </svg>
        <div className="arr-bu-donut-center">
          {active ? (
            <>
              <div className="arr-bu-donut-center-label">{active.row.label}</div>
              <div className="arr-bu-donut-center-val" style={{ color: SUB_PRODUCT_COLORS[active.i % SUB_PRODUCT_COLORS.length] }}>{fmt(active.row.value)}</div>
              <div className="arr-bu-donut-center-pct">{active.pctVal.toFixed(1)}% of total</div>
            </>
          ) : (
            <>
              <div className="arr-bu-donut-center-label">Total</div>
              <div className="arr-bu-donut-center-val">{fmt(total)}</div>
            </>
          )}
        </div>
      </div>
      <div className="arr-prod-legend">
        {segments.map((seg) => (
          <button
            key={seg.row.label}
            type="button"
            className={hoverIdx === seg.i ? 'active' : ''}
            onMouseEnter={() => setHoverIdx(seg.i)}
            onFocus={() => setHoverIdx(seg.i)}
          >
            <i style={{ background: SUB_PRODUCT_COLORS[seg.i % SUB_PRODUCT_COLORS.length] }} />{seg.row.label}
          </button>
        ))}
      </div>
    </div>
  );
}

function ContractionTrendChart({ data = [] }) {
  const rows = data.map((row) => ({
    month: row.month,
    churn: Math.abs(Number(row.churn || 0)),
    downsell: Math.abs(Number(row.downsell || 0)),
  }));
  const W = 900;
  const H = 230;
  const pad = { l: 64, r: 26, t: 18, b: 48 };
  const chartH = H - pad.t - pad.b;
  // The reference renders this as a STACKED bar (coStack() with both axes
  // stacked:true) — one bar per month, Downsell+FX stacked on top of Churn —
  // not two side-by-side bars. The Y-axis max must therefore be the combined
  // per-month total, not each series' own independent max.
  const maxV = Math.max(...rows.map((row) => row.churn + row.downsell), 1);
  const yMax = maxV < 1000 ? maxV : Math.ceil(maxV / 5000) * 5000;
  const slot = (W - pad.l - pad.r) / Math.max(1, rows.length);
  const barW = Math.min(46, Math.max(20, slot * 0.55));
  const toY = (value) => pad.t + chartH - (Number(value || 0) / yMax) * chartH;
  const ticks = [1, 0.8, 0.6, 0.4, 0.2, 0].map((t) => ({ value: yMax * t, y: toY(yMax * t) }));
  const baseline = pad.t + chartH;

  return (
    <div>
      <svg className="arr-trend-canvas" viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" style={{ height: 230 }}>
        {ticks.map((tick) => (
          <g key={tick.value}>
            <line x1={pad.l} x2={W - pad.r} y1={tick.y} y2={tick.y} stroke="rgba(0,0,0,.055)" />
            <text x={pad.l - 8} y={tick.y + 4} textAnchor="end" fontSize="10" fill="#8b9bb0" fontWeight="700">{fmt(tick.value)}</text>
          </g>
        ))}
        <line x1={pad.l} x2={pad.l} y1={pad.t} y2={pad.t + chartH} stroke="#d8e0ea" />
        <line x1={pad.l} x2={W - pad.r} y1={pad.t + chartH} y2={pad.t + chartH} stroke="#d8e0ea" />
        {rows.map((row, i) => {
          const center = pad.l + i * slot + slot / 2;
          const x = center - barW / 2;
          const churnTop = toY(row.churn);
          const stackTop = toY(row.churn + row.downsell);
          return (
            <g key={row.month}>
              <rect x={x} y={churnTop} width={barW} height={Math.max(0, baseline - churnTop)}
                fill="#dc2626cc" stroke="#dc2626" strokeWidth="1" rx="3" className="arr-vbar"
                style={{ transition: 'height .4s cubic-bezier(0.22, 1, 0.36, 1), y .4s cubic-bezier(0.22, 1, 0.36, 1), filter .15s ease' }}>
                <title>{`${row.month} Churn: ${fmt(row.churn)}`}</title>
              </rect>
              <rect x={x} y={stackTop} width={barW} height={Math.max(0, churnTop - stackTop)}
                fill="#f97316cc" stroke="#f97316" strokeWidth="1" rx="3" className="arr-vbar"
                style={{ transition: 'height .4s cubic-bezier(0.22, 1, 0.36, 1), y .4s cubic-bezier(0.22, 1, 0.36, 1), filter .15s ease' }}>
                <title>{`${row.month} Downsell+FX: ${fmt(row.downsell)}`}</title>
              </rect>
              <text x={center} y={H - 18} textAnchor="middle" fontSize="10" fill="#8b9bb0">{row.month}</text>
            </g>
          );
        })}
      </svg>
      <div className="arr-contract-legend">
        <span><i style={{ background: '#dc2626' }} />Churn</span>
        <span><i style={{ background: '#f97316' }} />Downsell+FX</span>
      </div>
    </div>
  );
}

function MultiTrendChart({ series = [], months = [], height = 290, colors = COLORS, rawMonthLabels = false, fillArea = false }) {
  const [hoverIdx, setHoverIdx] = useState(null);
  const visible = series.filter((item) => item.data?.some((point) => Number(point.value || 0) !== 0)).slice(0, 6);
  if (!visible.length || months.length < 2) {
    return <div className="arr-trend-canvas" style={{ height }} />;
  }
  const W = 900;
  const H = height;
  const pad = { l: 72, r: 26, t: 24, b: 42 };
  const allVals = visible.flatMap((item) => item.data.map((point) => Number(point.value || 0)));
  const minV = Math.min(0, ...allVals);
  const maxV = Math.max(1, ...allVals);
  const range = maxV - minV || 1;
  const plotW = W - pad.l - pad.r;
  const plotH = H - pad.t - pad.b;
  const x = (i) => pad.l + (i / Math.max(1, months.length - 1)) * plotW;
  const y = (v) => pad.t + plotH - ((Number(v || 0) - minV) / range) * plotH;
  const baseY = pad.t + plotH;
  const ticks = [0, 0.25, 0.5, 0.75, 1].map((t) => {
    const value = minV + range * t;
    return { value, y: y(value) };
  });
  // Reference tool shows the raw "YYYY-MM" label as-is (no Chart.js x-tick
  // formatter); mLbl()'s "Mon YY" abbreviation is only used where that
  // wasn't explicitly requested to change.
  const monthLabel = (month) => (rawMonthLabels ? month : mLbl(month));
  const colStep = plotW / Math.max(1, months.length - 1);

  // Compare-across-series hover tooltip (previously this chart had no hover
  // interaction at all — just a static dot on the last point).
  const tooltipW = 168;
  const tooltipH = 30 + visible.length * 18 + 10;
  const hoverX = hoverIdx != null ? x(hoverIdx) : null;
  const tooltipX = hoverX != null ? Math.min(Math.max(hoverX + 10, pad.l), W - tooltipW - 8) : 0;
  const tooltipY = pad.t + 6;

  return (
    <div>
      <div className="arr-wf-legend">
        {visible.map((item, i) => (
          <span className="arr-wf-legend-item" key={item.label}>
            <span className="arr-wf-dot" style={{ background: colors[i % colors.length] }} />{item.label}
          </span>
        ))}
      </div>
      <svg className="arr-trend-canvas" viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" style={{ height }}
        onMouseLeave={() => setHoverIdx(null)}>
        {ticks.map((tick) => (
          <g key={tick.value}>
            <line x1={pad.l} x2={W - pad.r} y1={tick.y} y2={tick.y} stroke="rgba(0,0,0,.05)" />
            <text x={pad.l - 10} y={tick.y + 4} textAnchor="end" fontSize="10" fill="#8898aa">{fmt(tick.value)}</text>
          </g>
        ))}
        <line x1={pad.l} x2={pad.l} y1={pad.t} y2={baseY} stroke="rgba(0,0,0,.08)" />
        <line x1={pad.l} x2={W - pad.r} y1={baseY} y2={baseY} stroke="rgba(0,0,0,.08)" />
        {visible.map((item, si) => {
          const points = item.data.map((point, i) => ({ x: x(i), y: y(point.value), ...point }));
          const line = points.map((point) => `${point.x.toFixed(1)},${point.y.toFixed(1)}`).join(' ');
          const color = colors[si % colors.length];
          return (
            <g key={item.label}>
              {fillArea && (
                <polygon
                  points={`${points[0].x.toFixed(1)},${baseY} ${line} ${points.at(-1).x.toFixed(1)},${baseY}`}
                  fill={color}
                  opacity="0.08"
                />
              )}
              <polyline points={line} fill="none" stroke={color} strokeWidth="2" vectorEffect="non-scaling-stroke" />
              {points.map((point, i) => (
                <circle key={point.month} cx={point.x} cy={point.y}
                  r={i === points.length - 1 || i === hoverIdx ? 3.5 : 2}
                  fill={color} />
              ))}
            </g>
          );
        })}
        {hoverIdx != null && (
          <line x1={hoverX} x2={hoverX} y1={pad.t} y2={baseY} stroke="rgba(0,0,0,.15)" strokeDasharray="3 3" />
        )}
        {/* Invisible per-month hit zones — wider than the line itself so
            hovering anywhere in a month's column shows that month's values. */}
        {months.map((month, i) => (
          <rect key={month} x={x(i) - colStep / 2} y={pad.t} width={colStep} height={plotH}
            fill="transparent"
            onMouseEnter={() => setHoverIdx(i)}
            onMouseMove={() => setHoverIdx(i)} />
        ))}
        {months.filter((_, i) => i === 0 || i === months.length - 1 || i % Math.ceil(months.length / 8) === 0).map((month) => (
          <text key={month} x={x(months.indexOf(month))} y={H - 14} textAnchor="middle" fontSize="9" fill="#8898aa">{monthLabel(month)}</text>
        ))}
        {hoverIdx != null && (
          <g pointerEvents="none">
            <rect x={tooltipX} y={tooltipY} width={tooltipW} height={tooltipH} rx="8" fill="rgba(31,31,31,.92)" />
            <text x={tooltipX + 12} y={tooltipY + 20} fill="#fff" fontSize="12.5" fontWeight="800" fontFamily="Inter, system-ui, sans-serif">
              {monthLabel(months[hoverIdx])}
            </text>
            {visible.map((item, si) => (
              <g key={item.label}>
                <rect x={tooltipX + 12} y={tooltipY + 30 + si * 18} width="8" height="8" rx="2" fill={colors[si % colors.length]} />
                <text x={tooltipX + 26} y={tooltipY + 38 + si * 18} fill="#fff" fontSize="10.5" fontFamily="Inter, system-ui, sans-serif">
                  {item.label}: {fmt(item.data[hoverIdx]?.value || 0)}
                </text>
              </g>
            ))}
          </g>
        )}
      </svg>
    </div>
  );
}

function LOBDrillPanel({ refData, lob, view, setView }) {
  const [chart1Mode, setChart1Mode] = useState('arr');
  const [selectedKey, setSelectedKey] = useState('');
  const [search, setSearch] = useState('');
  const [sortKey, setSortKey] = useState('arr');
  const [sortDir, setSortDir] = useState(-1);

  useEffect(() => {
    setSelectedKey('');
    setSearch('');
  }, [lob, view]);

  // Empty lob = "All" (every LOB combined) — no booking_type filter at all,
  // rather than treating it as "nothing selected".
  const filteredDeals = lob ? refData.deals.filter((deal) => (deal.booking_type || 'Unspecified') === lob) : refData.deals;
  const lobLabel = lob || 'All LOBs';
  const field = view === 'salesrep' ? 'sales_person' : view === 'subprod' ? 'sub_product' : 'end_user';
  const drillKey = useCallback(
    (deal) => (view === 'subprod' ? (deal.sub_product || 'Other') : (deal[field] || 'Unspecified')),
    [view, field]
  );
  const singularLabel = view === 'salesrep' ? 'Sales Rep' : view === 'subprod' ? 'Product' : 'Customer';
  const pluralLabel = `${singularLabel}s`;

  // Per-key aggregation matching the reference's gmap exactly: raw signed
  // churn (only summed while negative) + raw downsell, abs'd only at
  // display time — not the shared groupMetrics helper, which uses a
  // simple prev-month-only NRR base this panel doesn't need.
  const groups = useMemo(() => {
    const map = {};
    filteredDeals.forEach((deal) => {
      const key = drillKey(deal);
      if (!map[key]) map[key] = { key, arr: 0, prevArr: 0, newWon: 0, churn: 0 };
      const g = map[key];
      g.arr += Number(deal.arr?.[refData.latest] || 0);
      g.prevArr += Number(deal.arr?.[refData.prev] || 0);
      refData.allRangeMonths.forEach((month) => {
        const c = changeForDeal(deal, month);
        g.newWon += Math.max(0, Number(c.new || 0)) + Math.max(0, Number(c.upsell || 0));
        if (Number(c.churn || 0) < 0) g.churn += Number(c.churn);
        g.churn += Number(c.downsell || 0);
      });
    });
    return Object.values(map).map((g) => ({ ...g, mom: g.arr - g.prevArr }));
    // drillKey's identity changes whenever `view` does, so listing drillKey
    // alone is enough to re-run this when the Customers/Sales Reps/
    // Products toggle changes.
  }, [filteredDeals, refData.latest, refData.prev, refData.allRangeMonths, drillKey]);

  const groupsByArrDesc = useMemo(() => [...groups].sort((a, b) => b.arr - a.arr), [groups]);

  // Chart 1 (bar): order is ALWAYS by Closing ARR descending (matches the
  // reference's `groups` array, which is sorted once by arr and reused as-is
  // for both toggle modes) — only the bar VALUES swap between arr/newWon,
  // never the ranking. No zero-value filter either: the reference includes
  // every group in gmap regardless of whether the active metric is 0.
  const chart1Items = useMemo(() => {
    const metric = chart1Mode === 'arr' ? 'arr' : 'newWon';
    return groupsByArrDesc.map((g) => ({ label: g.key, value: g[metric] }));
  }, [groupsByArrDesc, chart1Mode]);

  // Chart 2 (trend): top-5-by-ARR over the FULL history (last 24 months with
  // any global ARR), not the period filter — matches the reference's
  // trendMonths = full all_months (filtered to ARR>0), sliced to 24.
  const trendMonths = useMemo(() => refData.playTrend.map((p) => p.month).slice(-24), [refData.playTrend]);
  const top5Trend = useMemo(() => {
    const top5 = groupsByArrDesc.slice(0, 5);
    return top5.map((g) => ({
      label: g.key,
      data: trendMonths.map((month) => ({
        month,
        value: filteredDeals.filter((deal) => drillKey(deal) === g.key).reduce((sum, deal) => sum + Number(deal.arr?.[month] || 0), 0),
      })),
    }));
  }, [groupsByArrDesc, trendMonths, filteredDeals, drillKey]);

  // Detail table: search + sortable columns.
  const tableRows = useMemo(() => {
    const q = search.trim().toLowerCase();
    const filtered = q ? groups.filter((g) => g.key.toLowerCase().includes(q)) : groups;
    return [...filtered].sort((a, b) => {
      if (sortKey === 'key') return sortDir < 0 ? b.key.localeCompare(a.key) : a.key.localeCompare(b.key);
      const av = Number(a[sortKey] || 0);
      const bv = Number(b[sortKey] || 0);
      return sortDir < 0 ? bv - av : av - bv;
    });
  }, [groups, search, sortKey, sortDir]);

  const totals = useMemo(() => tableRows.reduce((acc, row) => ({
    arr: acc.arr + row.arr,
    newWon: acc.newWon + row.newWon,
    churn: acc.churn + row.churn,
    mom: acc.mom + row.mom,
  }), { arr: 0, newWon: 0, churn: 0, mom: 0 }), [tableRows]);

  const toggleSort = (key) => {
    if (sortKey === key) setSortDir((d) => -d);
    else {
      setSortKey(key);
      setSortDir(-1);
    }
  };

  const DrillTh = ({ field: sortField, label, left }) => (
    <th onClick={() => toggleSort(sortField)} style={{ cursor: 'pointer', textAlign: left ? 'left' : 'right' }}>
      {label} <span style={{ fontSize: 9, color: '#94a3b8' }}>{sortKey === sortField ? (sortDir < 0 ? 'v' : '^') : '-'}</span>
    </th>
  );

  const selectedGroup = groups.find((g) => g.key === selectedKey) || null;
  const selectedDeals = useMemo(
    () => (selectedGroup ? filteredDeals.filter((deal) => drillKey(deal) === selectedGroup.key) : []),
    [selectedGroup, filteredDeals, drillKey]
  );

  // Row detail: last 12 (period-filtered) months' ARR, plus a secondary
  // cross-reference breakdown — Customer detail shows top Sales Reps,
  // Sales Rep/Product detail shows top Customers — matching the
  // reference's secView/secLabel logic exactly.
  const monthlyArrRows = useMemo(() => {
    if (!selectedGroup) return [];
    return refData.allRangeMonths.slice(-12).map((month) => ({
      month,
      value: selectedDeals.reduce((sum, deal) => sum + Number(deal.arr?.[month] || 0), 0),
    }));
  }, [selectedGroup, refData.allRangeMonths, selectedDeals]);

  const secondaryField = view === 'customer' ? 'sales_person' : 'end_user';
  const secondaryLabel = view === 'customer' ? 'Sales Rep' : 'Customer';
  const secondaryRows = useMemo(() => {
    if (!selectedGroup) return [];
    const map = {};
    selectedDeals.forEach((deal) => {
      const key = deal[secondaryField] || 'Unspecified';
      map[key] = (map[key] || 0) + Number(deal.arr?.[refData.latest] || 0);
    });
    return Object.entries(map)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 10);
  }, [selectedGroup, selectedDeals, secondaryField, refData.latest]);
  const secondaryMax = secondaryRows.length ? Math.abs(secondaryRows[0][1]) : 1;

  return (
    <div className="arr-lob-drill-body">
      <div className="arr-2col" style={{ marginBottom: 14 }}>
        <div className="arr-chart-card">
          <div className="arr-chart-hdr">
            <div>
              <div className="arr-chart-title">{lobLabel} — ARR by {singularLabel}</div>
              <div className="arr-card-note">{chart1Mode === 'arr' ? 'Closing ARR' : 'New + Expansion Won'} | click bar to drill in</div>
            </div>
            <ChartToggle
              value={chart1Mode}
              onChange={setChart1Mode}
              options={[
                { label: 'Closing ARR', value: 'arr' },
                { label: 'New Won', value: 'new' },
              ]}
            />
          </div>
          <div className="arr-lob-bar-scroll">
            <BarChart
              items={chart1Items}
              maxItems={chart1Items.length || 1}
              colors={DRILL_PALETTE}
              onItemClick={(item) => setSelectedKey(item.label)}
              activeLabel={selectedKey}
            />
          </div>
        </div>
        <div className="arr-chart-card">
          <div className="arr-chart-title" style={{ marginBottom: 2 }}>{lobLabel} — Top {pluralLabel} Trend</div>
          <div className="arr-card-note" style={{ marginBottom: 12 }}>Month on month ARR for top 5</div>
          <MultiTrendChart series={top5Trend} months={trendMonths} height={230} colors={DRILL_PALETTE} rawMonthLabels />
        </div>
      </div>

      <div className="arr-tcard">
        <div className="arr-tcard-hdr">
          <span className="arr-tcard-title">{lobLabel} Detail Table</span>
          <div className="arr-sub-controls">
            <input
              className="arr-search"
              placeholder="🔍 Search..."
              value={search}
              onChange={(event) => setSearch(event.target.value)}
            />
            <select
              className="arr-inline-select"
              value={selectedKey}
              onChange={(event) => setSelectedKey(event.target.value)}
            >
              <option value="">— Select {singularLabel} for detail —</option>
              {groupsByArrDesc.map((g) => <option key={g.key} value={g.key}>{g.key}</option>)}
            </select>
          </div>
        </div>
        <div className="arr-twrap">
          <table>
            <thead>
              <tr>
                <DrillTh field="key" label={singularLabel} left />
                <DrillTh field="arr" label="Closing ARR" />
                <DrillTh field="prevArr" label="Prev Month" />
                <DrillTh field="mom" label="MoM" />
                <DrillTh field="newWon" label="Period New+Up" />
                <DrillTh field="churn" label="Churn/Downsell" />
                <th style={{ textAlign: 'center' }}>Detail</th>
              </tr>
            </thead>
            <tbody>
              {tableRows.map((row) => (
                <tr key={row.key} className={selectedKey === row.key ? 'arr-row-selected' : ''}>
                  <td><strong>{selectedKey === row.key ? '▶ ' : ''}{row.key}</strong></td>
                  <td className="arr-td-money">{moneyDash(row.arr)}</td>
                  <td className="arr-td-money" style={{ color: '#718096' }}>{moneyDash(row.prevArr)}</td>
                  <td className={`arr-td-money ${row.mom > 0 ? 'arr-up' : row.mom < 0 ? 'arr-dn' : ''}`}>{arrowFull(row.mom)}</td>
                  <td className="arr-td-money arr-up">{moneyDash(row.newWon)}</td>
                  <td className="arr-td-money arr-dn">{moneyDash(Math.abs(row.churn))}</td>
                  <td style={{ textAlign: 'center' }}>
                    <button type="button" className="arr-drill-view-btn" onClick={() => setSelectedKey(row.key)}>View →</button>
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr>
                <td><strong>Total ({tableRows.length})</strong></td>
                <td className="arr-td-money"><strong>{fmtFull(totals.arr)}</strong></td>
                <td />
                <td className={`arr-td-money ${totals.mom >= 0 ? 'arr-up' : 'arr-dn'}`}><strong>{fmtFull(Math.abs(totals.mom))}</strong></td>
                <td className="arr-td-money arr-up"><strong>{fmtFull(totals.newWon)}</strong></td>
                <td className="arr-td-money arr-dn"><strong>{fmtFull(Math.abs(totals.churn))}</strong></td>
                <td />
              </tr>
            </tfoot>
          </table>
        </div>
      </div>

      {selectedGroup && (
        <div className="arr-drill-row-detail">
          <div className="arr-drill-row-detail-hdr">
            <div>
              <div className="arr-drill-row-detail-title">▶ {selectedGroup.key}</div>
              <div className="arr-drill-row-detail-sub">{lobLabel} | {singularLabel} detail | as of {refData.latest}</div>
            </div>
            <button type="button" className="arr-drill-row-detail-close" onClick={() => setSelectedKey('')}>✕ Close</button>
          </div>

          <div className="arr-drill-hero-stats">
            <div className="arr-drill-hero-stat" style={{ borderLeftColor: '#1e5fa8' }}>
              <div className="arr-drill-hero-label">Closing ARR</div>
              <div className="arr-drill-hero-val">{fmtFull(selectedGroup.arr)}</div>
            </div>
            <div className="arr-drill-hero-stat" style={{ borderLeftColor: selectedGroup.mom >= 0 ? '#15803d' : '#b91c1c' }}>
              <div className="arr-drill-hero-label">MoM</div>
              <div className="arr-drill-hero-val">{heroArrow(selectedGroup.mom)}</div>
            </div>
            <div className="arr-drill-hero-stat" style={{ borderLeftColor: '#15803d' }}>
              <div className="arr-drill-hero-label">Period New+Up</div>
              <div className="arr-drill-hero-val">{fmtFull(selectedGroup.newWon)}</div>
            </div>
            <div className="arr-drill-hero-stat" style={{ borderLeftColor: '#b91c1c' }}>
              <div className="arr-drill-hero-label">Churn/Downsell</div>
              <div className="arr-drill-hero-val">{fmtFull(Math.abs(selectedGroup.churn))}</div>
            </div>
          </div>

          <div className="arr-drill-section-title">Monthly ARR</div>
          <div className="arr-drill-monthly-row">
            {monthlyArrRows.map((row) => (
              <div className="arr-drill-monthly-cell" key={row.month}>
                <div className="arr-drill-monthly-month">{row.month}</div>
                <div className="arr-drill-monthly-val" style={{ color: row.value > 0 ? '#1e5fa8' : '#94a3b8' }}>
                  {row.value ? fmt(row.value) : '—'}
                </div>
              </div>
            ))}
          </div>

          <div className="arr-drill-section-title">By {secondaryLabel}</div>
          <div className="arr-drill-secondary-list">
            {secondaryRows.map(([label, value], i) => (
              <div className="arr-drill-secondary-row" key={label}>
                <div className="arr-drill-secondary-rank">{i + 1}</div>
                <div className="arr-drill-secondary-label">{label}</div>
                <div className="arr-drill-secondary-track">
                  <div className="arr-drill-secondary-fill" style={{ width: `${(Math.abs(value) / secondaryMax) * 100}%` }} />
                </div>
                <div className="arr-drill-secondary-val">{fmt(value)}</div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

const modalBarItems = (entries, color = '#1e5fa8', signed = false, limit = 6, basis = 'max') => {
  const rows = entries
    .filter(([, value]) => Number(value || 0) !== 0)
    .sort((a, b) => Math.abs(Number(b[1] || 0)) - Math.abs(Number(a[1] || 0)))
    .slice(0, limit);
  const denominator = typeof basis === 'number'
    ? Math.max(Math.abs(basis), 1)
    : Math.max(...rows.map(([, value]) => Math.abs(Number(value || 0))), 1);
  return rows.map(([label, value]) => ({
    label,
    value: signed ? signedModalMoney(value) : fmtModalMoney(Math.abs(value)),
    percent: Math.abs(Number(value || 0)) / denominator * 100,
    color: signed ? (Number(value || 0) >= 0 ? '#15803d' : '#b91c1c') : color,
  }));
};

function mapAdd(map, key, value) {
  if (!key || !Number(value || 0)) return;
  map[key] = (map[key] || 0) + Number(value || 0);
}

function movementMaps(ref, type) {
  const customer = {};
  const rep = {};
  const product = {};
  const lob = {};
  ref.deals.forEach((deal) => {
    ref.allRangeMonths.forEach((month) => {
      const c = changeForDeal(deal, month);
      // Reference's per-deal breakdowns (chMap in _buildBreakdowns) only count
      // a Churn/Downsell entry when it is ITSELF negative (e.g. chg.Downsell<0)
      // — never Math.abs() it unconditionally. Some contracts carry a second,
      // separate deal line with a POSITIVE Downsell/Churn (a correction/
      // reversal row from the source data); blindly abs'ing that would flip
      // its sign and double-count it as an extra loss instead of ignoring it.
      const churnVal = Number(c.churn || 0);
      const downsellVal = Number(c.downsell || 0);
      let value = 0;
      if (type === 'new') value = Math.max(0, Number(c.new || 0));
      if (type === 'upsell') value = Math.max(0, Number(c.upsell || 0));
      if (type === 'churn') value = churnVal < 0 ? churnVal : 0;
      if (type === 'downsell') value = downsellVal < 0 ? downsellVal : 0;
      if (type === 'retention-loss') value = (churnVal < 0 ? churnVal : 0) + (downsellVal < 0 ? downsellVal : 0);
      if (type === 'expansion') value = Math.max(0, Number(c.upsell || 0));
      if (!value) return;
      mapAdd(customer, deal.end_user, value);
      mapAdd(rep, deal.sales_person, value);
      mapAdd(product, deal.sub_product || 'Unknown', value);
      mapAdd(lob, deal.booking_type || 'Unknown', value);
    });
  });
  return { customer, rep, product, lob };
}

function currentArrMaps(ref) {
  const customer = {};
  const product = {};
  const lob = {};
  const rep = {};
  ref.deals.forEach((deal) => {
    const value = Number(deal.arr?.[ref.latest] || 0);
    if (!value) return;
    mapAdd(customer, deal.end_user, value);
    mapAdd(product, deal.sub_product || 'Unknown', value);
    mapAdd(lob, deal.booking_type || 'Unknown', value);
    mapAdd(rep, deal.sales_person || 'Unassigned', value);
  });
  return { customer, product, lob, rep };
}

function periodMoverEntries(ref) {
  // ref.periodStartARR comes from the backend's _compute_period_metrics,
  // whose opening baseline is the month BEFORE ref.from (e.g. Dec of the
  // prior year for the default 'ytd' period, per dashboard.py's
  // _period_range/_previous_month) — NOT a rolling 12-months-back window
  // despite the "ltm_" field-name prefix. Match that exact baseline so the
  // mover totals reconcile with the Period Start ARR / Net Change stats.
  const baseMonth = getPrevMonth(ref.from);
  const customer = {};
  ref.deals.forEach((deal) => {
    const delta = Number(deal.arr?.[ref.latest] || 0) - Number(deal.arr?.[baseMonth] || 0);
    if (!delta) return;
    mapAdd(customer, deal.end_user, delta);
  });
  const all = Object.entries(customer)
    .filter(([, value]) => Number(value || 0) !== 0)
    .sort((a, b) => Math.abs(Number(b[1] || 0)) - Math.abs(Number(a[1] || 0)));
  const gainers = all.filter(([, value]) => Number(value) > 0);
  const losers = all.filter(([, value]) => Number(value) < 0);
  const max = all.length ? Math.abs(Number(all[0][1])) : 1;
  return { all, gainers, losers, max };
}

// Downsell change entries can split across multiple deal rows for the same
// customer+month (e.g. a renewal's old line term-ending negative, new line
// term-starting positive) — these must net to zero BEFORE taking abs, or a
// customer gets double-counted as downsell when they actually just renewed.
function downsellCustomerMaps(ref) {
  const custMonthNet = {};
  const custRepOf = {};
  const custProdOf = {};
  ref.deals.forEach((deal) => {
    ref.allRangeMonths.forEach((month) => {
      const c = changeForDeal(deal, month);
      const net = Number(c.downsell || 0);
      if (!net) return;
      const key = `${deal.end_user}||${month}`;
      custMonthNet[key] = (custMonthNet[key] || 0) + net;
      if (!custRepOf[deal.end_user]) custRepOf[deal.end_user] = deal.sales_person || 'Unassigned';
      if (!custProdOf[deal.end_user]) custProdOf[deal.end_user] = deal.sub_product || 'Unknown';
    });
  });
  const customer = {};
  const rep = {};
  const product = {};
  Object.keys(custMonthNet).forEach((key) => {
    const net = custMonthNet[key];
    if (net >= 0) return;
    const lost = Math.abs(net);
    const cust = key.split('||')[0];
    mapAdd(customer, cust, lost);
    mapAdd(rep, custRepOf[cust], lost);
    mapAdd(product, custProdOf[cust], lost);
  });
  return { customer, rep, product };
}

function makeKpiDetails(ref) {
  const arr = currentArrMaps(ref);
  const newMaps = movementMaps(ref, 'new');
  const upsellMaps = movementMaps(ref, 'upsell');
  const churnMaps = movementMaps(ref, 'churn');
  const downsellCust = downsellCustomerMaps(ref);
  const lossMaps = movementMaps(ref, 'retention-loss');
  const expansionMaps = movementMaps(ref, 'expansion');
  const movers = periodMoverEntries(ref);
  const activeLogos = Object.keys(arr.customer).length;
  // Reference's "By Product" breakdown for the Total ARR card computes
  // each bar's percent against the SUM OF THE TOP 5 sub-products shown, not
  // the dashboard's grand total ARR (unlike "By Type", which sums ALL types
  // since booking_type always covers 100% of deals) — match that basis
  // exactly rather than reusing ref.curr.
  const prodTop5 = Object.entries(arr.product)
    .filter(([label, value]) => label !== 'Unknown' && Number(value || 0) > 0)
    .sort((a, b) => Number(b[1]) - Number(a[1]))
    .slice(0, 5);
  const prodTop5Total = prodTop5.reduce((sum, [, value]) => sum + Number(value || 0), 0) || 1;

  return {
    total: {
      title: 'Total ARR',
      formula: `Annual Recurring Revenue - all active subscriptions as of ${ref.latest || 'latest month'}`,
      big: fmtModalMoney(ref.curr),
      color: '#7c3aed',
      stats: [
        { label: 'Closing ARR', value: fmtModalMoney(ref.curr), color: '#1e5fa8' },
        { label: 'Prior Month', value: fmtModalMoney(ref.prv), color: '#718096' },
        { label: 'MoM Change', value: signedModalMoney(ref.curr - ref.prv), color: ref.curr - ref.prv >= 0 ? '#15803d' : '#b91c1c' },
        { label: 'Active Customers', value: `${activeLogos} accounts`, color: '#059669' },
      ],
      insight: 'ARR is the total annualised value of active contracts. Growing ARR = more customers staying + new ones joining.',
      benchmark: 'Target: positive growth every month',
      sections: [
        // Percent is share of Total ARR (curr), not relative to the largest
        // customer shown — per explicit request, overriding the reference
        // tool's own "% of largest customer" convention for this section.
        { title: '👤 Customers making up this ARR', color: '#1e5fa8', items: modalBarItems(Object.entries(arr.customer), '#1e5fa8', false, activeLogos, ref.curr) },
        { title: '🏷️ By Type', color: '#6d28d9', items: modalBarItems(Object.entries(arr.lob), '#6d28d9', false, 5, ref.curr) },
        { title: '📦 By Product', color: '#0891b2', items: modalBarItems(prodTop5, '#0891b2', false, 5, prodTop5Total) },
      ],
    },
    change: {
      title: `${ref.periodLabel} Period Change`,
      formula: 'Net ARR change vs period start',
      big: signedModalMoney(ref.periodChange),
      color: ref.periodChange >= 0 ? '#15803d' : '#b91c1c',
      stats: [
        { label: 'Period End ARR', value: fmtModalMoney(ref.curr), color: '#0f2d52' },
        { label: 'Period Start ARR', value: fmtModalMoney(ref.periodStartARR), color: '#718096' },
        { label: 'Net Change', value: signedModalMoney(ref.periodChange), color: ref.periodChange >= 0 ? '#15803d' : '#b91c1c' },
        { label: 'Change %', value: ref.periodStartARR > 0 ? `${ref.periodChangePct.toFixed(1)}%` : '—', color: ref.periodChange >= 0 ? '#15803d' : '#b91c1c' },
      ],
      insight: 'Positive change = more ARR came in (new deals, expansions) than went out (churn). Target: stay green every period.',
      benchmark: 'Healthy: +5%+ | OK: 0–5% | Concern: Negative',
      note: {
        bold: 'Net Change',
        text: ' = closing ARR vs period start. A customer with a downsell event can still appear as a net grower if their expansions exceeded the downsell in the same period.',
        accent: '#1e5fa8',
        bg: '#eff6ff',
      },
      // Reference uses a separate local label just for these two section
      // titles ("this period" when the filter is custom) — distinct from
      // periodLabel's "Period" used everywhere else (card/modal titles).
      sections: (() => {
        const periodTitle = ref.periodLabel === 'Period' ? 'this period' : ref.periodLabel;
        return [
          // Percent is each mover's share of the overall Net Change (periodChange),
          // not relative to the single largest mover — per explicit request.
          { title: `📈 ${periodTitle}: Expansion + New Bookings`, color: '#15803d', items: modalBarItems(movers.gainers, '#15803d', true, movers.gainers.length || 1, ref.periodChange) },
          { title: `📉 ${periodTitle}: Downsell + Churn Contractions`, color: '#b91c1c', items: modalBarItems(movers.losers, '#b91c1c', true, 5, ref.periodChange) },
        ];
      })(),
    },
    bookings: {
      title: `${ref.periodLabel} Bookings (New + Expansion)`,
      formula: 'New ARR + Expansion ARR — total new revenue won this period',
      big: fmtModalMoney(ref.pNew + ref.pUp),
      color: '#1e5fa8',
      stats: [
        { label: 'New Bookings', value: fmtModalMoney(ref.pNew), color: '#059669' },
        { label: 'Expansion Bookings', value: fmtModalMoney(ref.pUp), color: '#0891b2' },
        { label: 'Total Bookings', value: fmtModalMoney(ref.pNew + ref.pUp), color: '#1e5fa8' },
        { label: '% of Total ARR', value: ref.curr > 0 ? `${(((ref.pNew + ref.pUp) / ref.curr) * 100).toFixed(1)}%` : '—', color: '#0f2d52' },
      ],
      insight: 'Bookings combine new-logo wins and expansion revenue from existing customers — the total new ARR added this period.',
      benchmark: 'Healthy: Bookings growing period over period',
      sections: (() => {
        const bookingsByRep = {};
        Object.entries(newMaps.rep).forEach(([rep, value]) => mapAdd(bookingsByRep, rep, value));
        Object.entries(upsellMaps.rep).forEach(([rep, value]) => mapAdd(bookingsByRep, rep, value));
        return [
          { title: '🆕 New Logo Customers', color: '#059669', items: modalBarItems(Object.entries(newMaps.customer), '#059669', false, Object.keys(newMaps.customer).length || 1, ref.pNew) },
          { title: '⬆️ Customers with Expansion', color: '#0891b2', items: modalBarItems(Object.entries(upsellMaps.customer), '#0891b2', false, Object.keys(upsellMaps.customer).length || 1, ref.pUp) },
          { title: '🧑‍💼 By Sales Rep (Total Bookings)', color: '#1e5fa8', items: modalBarItems(Object.entries(bookingsByRep), '#1e5fa8', false, 5, ref.pNew + ref.pUp) },
        ];
      })(),
    },
    new: {
      title: `${ref.periodLabel} New Booking`,
      formula: 'Revenue from brand-new logos and first-time bookings this period',
      big: fmtModalMoney(ref.pNew),
      color: '#059669',
      stats: [
        { label: 'New ARR', value: fmtModalMoney(ref.pNew), color: '#059669' },
        { label: 'New Logos', value: ref.pNew > 0 ? 'See breakdown below' : '—', color: '#059669' },
        { label: '% of Total ARR', value: ref.curr > 0 ? `${((ref.pNew / ref.curr) * 100).toFixed(1)}%` : '—', color: '#0f2d52' },
        { label: 'vs Expansion', value: ref.pUp > 0 ? `${fmtModalMoney(ref.pNew)} vs ${fmtModalMoney(ref.pUp)}` : '—', color: '#6b7280' },
      ],
      insight: 'New ARR = revenue from customers who had zero ARR before. This is your new logo engine.',
      benchmark: 'Healthy: New ARR growing month-over-month',
      sections: [
        // Percent is each row's share of the New ARR total (pNew), not
        // relative to the largest single row — per explicit request.
        { title: '🆕 New Logo Customers', color: '#059669', items: modalBarItems(Object.entries(newMaps.customer), '#059669', false, Object.keys(newMaps.customer).length || 1, ref.pNew) },
        { title: '🧑‍💼 By Sales Rep (New ARR)', color: '#059669', items: modalBarItems(Object.entries(newMaps.rep), '#059669', false, 5, ref.pNew) },
        { title: '📦 By Product (New ARR)', color: '#059669', items: modalBarItems(Object.entries(newMaps.product), '#059669', false, 5, ref.pNew) },
      ],
    },
    upsell: {
      title: `${ref.periodLabel} Expansion Bookings`,
      formula: 'Expansion revenue from existing customers this period',
      big: fmtModalMoney(ref.pUp),
      color: '#0891b2',
      stats: [
        { label: 'Expansion ARR', value: fmtModalMoney(ref.pUp), color: '#0891b2' },
        { label: '% of Total ARR', value: ref.curr > 0 ? `${((ref.pUp / ref.curr) * 100).toFixed(1)}%` : '—', color: '#0f2d52' },
        { label: 'vs New ARR', value: ref.pNew > 0 ? `${fmtModalMoney(ref.pUp)} vs ${fmtModalMoney(ref.pNew)}` : '—', color: '#6b7280' },
        { label: 'Expansion Ratio', value: (ref.pNew + ref.pUp) > 0 ? `${((ref.pUp / (ref.pNew + ref.pUp)) * 100).toFixed(1)}% of wins` : '—', color: '#0891b2' },
      ],
      insight: 'Expansion = existing customers spending more. High expansion = strong product-market fit and customer success.',
      benchmark: 'Best-in-class: Expansion > New ARR (land-and-expand motion)',
      sections: [
        // Percent is each row's share of the Expansion total (pUp), not
        // relative to the largest single row — per explicit request.
        { title: '⬆️ Customers with Expansion', color: '#0891b2', items: modalBarItems(Object.entries(upsellMaps.customer), '#0891b2', false, Object.keys(upsellMaps.customer).length || 1, ref.pUp) },
        { title: '🧑‍💼 By Sales Rep (Expansion)', color: '#0891b2', items: modalBarItems(Object.entries(upsellMaps.rep), '#0891b2', false, 5, ref.pUp) },
        { title: '📦 By Product (Expansion)', color: '#0891b2', items: modalBarItems(Object.entries(upsellMaps.product), '#0891b2', false, 5, ref.pUp) },
      ],
    },
    contractions: {
      title: `${ref.periodLabel} Contractions (Churn + Downsell)`,
      formula: 'Churn ARR + Downsell ARR — total ARR shrinkage this period',
      big: fmtModalMoney(Math.abs(ref.pChurn) + ref.pDn),
      color: '#dc2626',
      stats: [
        { label: 'Churned ARR', value: fmtModalMoney(Math.abs(ref.pChurn)), color: '#dc2626' },
        { label: 'Downsell ARR', value: fmtModalMoney(ref.pDn), color: '#d97706' },
        { label: 'Total Contraction', value: fmtModalMoney(Math.abs(ref.pChurn) + ref.pDn), color: '#dc2626' },
        { label: '% of Total ARR', value: ref.curr > 0 ? `${(((Math.abs(ref.pChurn) + ref.pDn) / ref.curr) * 100).toFixed(1)}%` : '—', color: '#0f2d52' },
      ],
      insight: 'Contractions combine full cancellations (churn) and reduced spend from existing customers (downsell) — total ARR shrinkage this period.',
      benchmark: 'Healthy: Contractions shrinking period over period',
      sections: [
        // Percent is each row's share of Total Contraction (|Churn| + Downsell),
        // not relative to the largest single row — matches the GRR card's
        // basis for the same lossMaps data.
        { title: '🔴 Customers driving contraction', color: '#dc2626', items: modalBarItems(Object.entries(lossMaps.customer), '#dc2626', true, Object.keys(lossMaps.customer).length || 1, Math.abs(ref.pChurn) + ref.pDn) },
        { title: '🧑‍💼 By Sales Rep (Contraction)', color: '#dc2626', items: modalBarItems(Object.entries(lossMaps.rep), '#dc2626', true, 5, Math.abs(ref.pChurn) + ref.pDn) },
        { title: '📦 By Product (Contraction)', color: '#dc2626', items: modalBarItems(Object.entries(lossMaps.product), '#dc2626', true, 5, Math.abs(ref.pChurn) + ref.pDn) },
      ],
    },
    churn: {
      title: `${ref.periodLabel} Churn`,
      formula: 'Revenue shrinkage from customers who cancelled entirely',
      big: fmtModalMoney(Math.abs(ref.pChurn)),
      color: '#dc2626',
      stats: [
        { label: 'Churned ARR', value: fmtModalMoney(Math.abs(ref.pChurn)), color: '#dc2626' },
        { label: 'Churn Rate', value: ref.prv > 0 ? `${((Math.abs(ref.pChurn) / ref.prv) * 100).toFixed(1)}%` : '—', color: ref.prv > 0 && (Math.abs(ref.pChurn) / ref.prv) * 100 > 5 ? '#dc2626' : '#059669' },
        { label: 'vs Downsell', value: fmtModalMoney(ref.pDn), color: '#d97706' },
        { label: 'Total Shrinkage', value: fmtModalMoney(Math.abs(ref.pChurn) + ref.pDn), color: '#dc2626' },
      ],
      insight: 'Churn = customers who cancelled 100% of their contract. This is the most damaging ARR loss.',
      benchmark: 'Healthy: <5% annual churn | Concern: >10% annual churn',
      sections: [
        // Percent is each row's share of Churned ARR (pChurn), not relative
        // to the largest single row — per explicit request.
        { title: '🔴 Customers who churned (cancelled)', color: '#dc2626', items: modalBarItems(Object.entries(churnMaps.customer), '#dc2626', true, Object.keys(churnMaps.customer).length || 1, Math.abs(ref.pChurn)) },
        { title: '🧑‍💼 By Sales Rep (Churn)', color: '#dc2626', items: modalBarItems(Object.entries(churnMaps.rep), '#dc2626', true, 5, Math.abs(ref.pChurn)) },
        { title: '📦 By Product (Churn)', color: '#dc2626', items: modalBarItems(Object.entries(churnMaps.product), '#dc2626', true, 5, Math.abs(ref.pChurn)) },
      ],
    },
    downsell: {
      title: `${ref.periodLabel} Downsell`,
      formula: 'Revenue shrinkage from customers who reduced their contract',
      big: fmtModalMoney(ref.pDn),
      color: '#d97706',
      stats: [
        { label: 'Downsell ARR', value: fmtModalMoney(ref.pDn), color: '#d97706' },
        { label: 'Downsell Rate', value: ref.prv > 0 ? `${((ref.pDn / ref.prv) * 100).toFixed(1)}%` : '—', color: ref.prv > 0 && (ref.pDn / ref.prv) * 100 > 3 ? '#d97706' : '#059669' },
        { label: 'vs Churn', value: fmtModalMoney(Math.abs(ref.pChurn)), color: '#dc2626' },
        { label: 'Total Contraction', value: fmtModalMoney(Math.abs(ref.pChurn) + ref.pDn), color: '#dc2626' },
      ],
      insight: 'Downsell = customers who stayed but reduced spend. Often a warning sign before full churn.',
      benchmark: 'Healthy: <3% downsell rate | Watch: >5%',
      note: {
        bold: 'Downsell card',
        text: ' = customers with a downsell event recorded. A customer here may still be a net grower in the YTD Change card if expansions exceeded their downsell.',
        accent: '#d97706',
        bg: '#fff7ed',
      },
      sections: [
        // Percent is each row's share of Downsell ARR (pDn), not relative
        // to the largest single row — per explicit request.
        { title: '🟠 Customers who downselled (reduced contract)', color: '#d97706', items: modalBarItems(Object.entries(downsellCust.customer).map(([k, v]) => [k, -v]), '#d97706', true, Object.keys(downsellCust.customer).length || 1, ref.pDn) },
        { title: '🧑‍💼 By Sales Rep (Downsell)', color: '#d97706', items: modalBarItems(Object.entries(downsellCust.rep).map(([k, v]) => [k, -v]), '#d97706', true, 5, ref.pDn) },
        { title: '📦 By Product (Downsell)', color: '#d97706', items: modalBarItems(Object.entries(downsellCust.product).map(([k, v]) => [k, -v]), '#d97706', true, 5, ref.pDn) },
      ],
    },
    growth: {
      title: `${ref.periodLabel} ARR Growth`,
      formula: '(Period End ARR − Period Start ARR) ÷ Period Start ARR × 100',
      big: `${ref.periodChangePct >= 0 ? '+' : ''}${ref.periodChangePct.toFixed(2)}%`,
      color: ref.periodChangePct >= 0 ? '#15803d' : '#b91c1c',
      stats: [
        { label: 'Period End ARR', value: fmtModalMoney(ref.curr), color: '#1e5fa8' },
        { label: 'Period Start ARR', value: fmtModalMoney(ref.periodStartARR), color: '#718096' },
        { label: 'Net Change', value: `${ref.periodChange >= 0 ? '+' : ''}${fmtModalMoney(Math.abs(ref.periodChange))}`, color: ref.periodChange >= 0 ? '#15803d' : '#b91c1c' },
        { label: 'Growth %', value: `${ref.periodChangePct >= 0 ? '+' : ''}${ref.periodChangePct.toFixed(2)}%`, color: ref.periodChangePct >= 0 ? '#15803d' : '#b91c1c' },
      ],
      insight: 'Month-on-Month growth tells you the velocity of your business. Consistently positive = scaling. Negative = contraction.',
      benchmark: 'Healthy: +5%+ MoM | OK: 0–5% | Concern: Negative',
      sections: [
        // Must use `movers` (periodMoverEntries: latest vs. the SAME Dec-2025
        // -style period-start baseline as Net Change above) — NOT `mom`
        // (momMaps: latest vs. just last month). Using the single-month delta
        // here made this section show a handful of few-thousand-dollar movers
        // that couldn't possibly explain a multi-million-dollar Net Change,
        // since it was answering a completely different question than the
        // one this section's title asks. Show only positive customer growth;
        // declines belong in the YTD Change breakdown. Percent is each
        // grower's share of the overall Net Change (periodChange).
        { title: '📊 Customers driving this growth rate', color: '#1e5fa8', items: modalBarItems(movers.gainers, '#1e5fa8', true, movers.gainers.length || 1, ref.periodChange) },
      ],
    },
    grr: {
      title: 'GRR% — Gross Revenue Retention',
      formula: '(Prior ARR − Churn − Downsell) ÷ Prior ARR × 100',
      big: `${ref.grr.toFixed(2)}%`,
      color: ref.grr >= 90 ? '#15803d' : ref.grr >= 75 ? '#d97706' : '#b91c1c',
      stats: [
        { label: 'Prior Period ARR', value: fmtModalMoney(ref.nrrBase || ref.prv), color: '#1e5fa8' },
        { label: 'Churned ARR', value: fmtModalMoney(Math.abs(ref.pChurn)), color: '#b91c1c' },
        { label: 'Downsell/FX', value: fmtModalMoney(Math.abs(ref.pDn)), color: '#ea580c' },
        { label: 'GRR %', value: `${ref.grr.toFixed(2)}%`, color: ref.grr >= 90 ? '#15803d' : '#b91c1c' },
      ],
      insight: 'GRR shows what % of prior revenue you KEPT — without counting expansions. 100% = zero churn. Lower = more revenue leaving.',
      benchmark: 'Best-in-class: 95%+ | Healthy: 90–95% | Concern: Below 85%',
      sections: (() => {
        // Percent is each row's share of Total Lost (|Churn| + Downsell),
        // not relative to the largest single row — per explicit request.
        const totalLost = Math.abs(ref.pChurn) + ref.pDn;
        return [
          {
            title: '⚠️ Customers dragging GRR down',
            color: '#b91c1c',
            emptyText: '✅ No churn this period — GRR is healthy',
            emptyColor: '#15803d',
            items: modalBarItems(Object.entries(lossMaps.customer), '#b91c1c', true, Object.keys(lossMaps.customer).length || 1, totalLost),
          },
          { title: '🏷️ By Type', color: '#ea580c', items: modalBarItems(Object.entries(lossMaps.lob), '#ea580c', true, Object.keys(lossMaps.lob).length || 1, totalLost) },
          { title: '📦 By Product', color: '#ea580c', items: modalBarItems(Object.entries(lossMaps.product), '#ea580c', true, 5, totalLost) },
        ];
      })(),
    },
    nrr: {
      title: 'NRR% — Net Revenue Retention (incl. Expansion)',
      formula: '(Prior ARR − Churn − Downsell + Expansion) ÷ Prior ARR × 100',
      big: `${ref.nrr.toFixed(2)}%`,
      color: ref.nrr >= 110 ? '#15803d' : ref.nrr >= 100 ? '#d97706' : '#b91c1c',
      stats: [
        { label: 'Prior Period ARR', value: fmtModalMoney(ref.nrrBase || ref.prv), color: '#1e5fa8' },
        { label: 'Expansion', value: fmtModalMoney(ref.pUp), color: '#15803d' },
        { label: 'Churn+Downsell', value: fmtModalMoney(Math.abs(ref.pChurn) + ref.pDn), color: '#b91c1c' },
        { label: 'NRR %', value: `${ref.nrr.toFixed(2)}%`, color: ref.nrr >= 110 ? '#15803d' : '#b91c1c' },
      ],
      insight: 'NRR >100% means existing customers are spending MORE than before. Expansion revenue exceeds losses from churn. This is the holy grail — you grow even with zero new logos.',
      benchmark: 'Elite: 120%+ | Great: 110–120% | Good: 100–110% | Concern: <100%',
      // Percent is each row's share of the relevant total — Expansion (pUp) for
      // the expansion sections, Total Lost (|Churn|+Downsell) for the
      // contraction section — not relative to the largest single row, per
      // explicit request.
      sections: (() => {
        const totalLost = Math.abs(ref.pChurn) + ref.pDn;
        return [
          {
            title: '⬆️ Customers expanding (Expansion → boosting NRR)',
            color: '#15803d',
            emptyText: 'No expansions this period',
            items: modalBarItems(Object.entries(expansionMaps.customer), '#15803d', false, 5, ref.pUp),
          },
          {
            title: '🧑‍💼 Sales Reps driving Expansion',
            color: '#0f766e',
            items: modalBarItems(Object.entries(expansionMaps.rep), '#0f766e', false, 5, ref.pUp),
          },
          {
            title: '⬇️ Customers reducing NRR (Churn/Downsell)',
            color: '#b91c1c',
            emptyText: '✅ No churn this period',
            emptyColor: '#15803d',
            items: modalBarItems(Object.entries(lossMaps.customer), '#b91c1c', true, 5, totalLost),
          },
        ];
      })(),
    },
  };
}

function ArrDashTab({ data, onSelectCustomer, onSelectRep }) {
  const records = useMemo(() => data.records || [], [data.records]);
  const ref = useMemo(() => {
    const base = buildReferenceView(records, data.period || {});
    const kpis = data.kpis || {};
    const apiTrend = data.trend || [];

    // Backend is authoritative for latest month and KPI values
    const apiLatest = apiTrend.length ? apiTrend.at(-1).month : base.latest;
    const prev = getPrevMonth(apiLatest);
    const curr = base.deals.reduce((s, d) => s + Number(d.arr?.[apiLatest] || 0), 0);
    const prv = base.deals.reduce((s, d) => s + Number(d.arr?.[prev] || 0), 0);

    const pNew = Number(kpis.ltm_new_arr || 0);
    const pUp = Number(kpis.ltm_upsell || 0);
    const pChurn = -Math.abs(Number(kpis.ltm_churn || 0));
    const pDn = Math.abs(Number(kpis.ltm_downsell || 0));
    const grr = Number(kpis.grr ?? 100);
    const nrr = Number(kpis.nrr ?? 100);
    const ltmOpening = Number(kpis.ltm_opening_arr || 0);
    const periodChange = curr - ltmOpening;
    const periodChangePct = ltmOpening > 0 ? (periodChange / ltmOpening) * 100 : 0;

    const from = data.period?.from || base.from;
    const to = data.period?.to || apiLatest;
    const allRangeMonths = base.allMonths.filter(m => (!from || m >= from) && (!to || m <= to));
    const periodValue = String(data.period?.value || 'ytd').toLowerCase();
    const periodLabel = periodValue === 'custom' ? 'Period' : periodValue.toUpperCase();

    const trend = apiTrend
      .filter(t => Number(t.value) > 0)
      .map(t => ({ month: t.month, value: Number(t.value || 0) }));
    const waterfall = base.waterfall;

    return {
      ...base,
      latest: apiLatest,
      prev,
      curr,
      prv,
      pNew,
      pUp,
      pChurn,
      pDn,
      grr,
      nrr,
      nrrBase: ltmOpening,
      periodStartARR: ltmOpening,
      periodChange,
      periodChangePct,
      waterfall,
      trend,
      from,
      to,
      allRangeMonths,
      periodLabel,
    };
  }, [records, data]);
  const [sortField, setSortField] = useState('curr_arr');
  // sortDir=1 sorts numeric fields highest-first (see customerRows below:
  // sortDir * (bv - av) puts the larger value first when sortDir is
  // positive) — default to Current ARR, highest to lowest.
  const [sortDir, setSortDir] = useState(1);
  const [search, setSearch] = useState('');
  const [expanded, setExpanded] = useState(null);
  const [buMode, setBuMode] = useState('arr');
  const [repMode, setRepMode] = useState('arr');
  const [quarterMode, setQuarterMode] = useState('new');
  const [subView, setSubView] = useState('table');
  const [subSelect, setSubSelect] = useState('');
  const [subTblSortKey, setSubTblSortKey] = useState('value');
  const [subTblSortDir, setSubTblSortDir] = useState(-1);
  const [lobTrendMode, setLobTrendMode] = useState('arr');
  const [lobDrill, setLobDrill] = useState('');
  const [lobDrillView, setLobDrillView] = useState('customer');
  const [grrGaugeOpen, setGrrGaugeOpen] = useState(false);
  const [nrrGaugeOpen, setNrrGaugeOpen] = useState(false);
  const [productDonutMode, setProductDonutMode] = useState('product');
  const [productTrendMode, setProductTrendMode] = useState('product');
  const details = useMemo(() => makeKpiDetails(ref), [ref]);

  // Scoped only to the "ARR by Product" donut card's own Product / Product
  // Category toggle — kept independent from productTrendGroups below so the
  // two cards can be switched between Product and Product Category on their
  // own, without affecting each other.
  const productDonutGroups = useMemo(
    () => groupProducts(
      ref.deals, ref.latest, ref.allRangeMonths,
      productDonutMode === 'category' ? 'product_category' : 'sub_product'
    ),
    [ref.deals, ref.latest, ref.allRangeMonths, productDonutMode]
  );

  // Same idea, independently scoped to the "Product ARR - Trend & Table"
  // card's own Product / Product Category toggle — also drives its dropdown
  // options and drill-down trend below.
  const productTrendField = productTrendMode === 'category' ? 'product_category' : 'sub_product';
  const productTrendGroups = useMemo(
    () => groupProducts(ref.deals, ref.latest, ref.allRangeMonths, productTrendField),
    [ref.deals, ref.latest, ref.allRangeMonths, productTrendField]
  );

  const groups = useMemo(() => {
    const bu = groupMetrics(ref.deals, ref.latest, ref.prev, ref.allRangeMonths, 'region');
    const reps = groupMetrics(ref.deals, ref.latest, ref.prev, ref.allRangeMonths, 'sales_person');
    const lob = groupMetrics(ref.deals, ref.latest, ref.prev, ref.allRangeMonths, 'booking_type');
    const customers = groupCurrent(ref.deals, ref.latest, 'end_user').slice(0, 20);
    return { bu, reps, lob, customers };
  }, [ref]);

  // Dedicated derivation for the ARR-by-LOB cards, mirroring the reference's
  // own KPI-card builder exactly rather than reusing groups.lob (which is
  // sorted by value and uses a simple prev-month NRR base — fine for the LOB
  // drill-down dropdown it also feeds, but not what these cards need):
  // alphabetical order, the reference's own color palette, and an NRR base
  // anchored to the month BEFORE the period's first month (true period-start
  // retention), not just the prior single month.
  const lobCards = useMemo(() => {
    const types = [...new Set(ref.deals.map((deal) => deal.booking_type).filter(Boolean))].sort();
    const prevMonth = ref.prev;
    const periodStartM = ref.allRangeMonths.length ? getPrevMonth(ref.allRangeMonths[0]) : prevMonth;
    const tot = ref.deals.reduce((sum, deal) => sum + Number(deal.arr?.[ref.latest] || 0), 0);
    return types.map((bt, i) => {
      const deals = ref.deals.filter((deal) => deal.booking_type === bt);
      const arr = deals.reduce((sum, deal) => sum + Number(deal.arr?.[ref.latest] || 0), 0);
      const prevArr = deals.reduce((sum, deal) => sum + Number(deal.arr?.[prevMonth] || 0), 0);
      let pNew = 0, pUp = 0, pChurn = 0, pDn = 0;
      ref.allRangeMonths.forEach((month) => {
        deals.forEach((deal) => {
          const c = changeForDeal(deal, month);
          if (Number(c.new || 0) > 0) pNew += Number(c.new);
          if (Number(c.upsell || 0) > 0) pUp += Number(c.upsell);
          if (Number(c.churn || 0) < 0) pChurn += Math.abs(Number(c.churn));
          if (Number(c.downsell || 0) < 0) pDn += Math.abs(Number(c.downsell));
        });
      });
      const nrrBaseArr = deals.reduce((sum, deal) => sum + Number(deal.arr?.[periodStartM] || 0), 0);
      const nrrBase = nrrBaseArr || prevArr;
      const nrr = nrrBase > 0 ? ((nrrBase - pChurn - pDn + pUp) / nrrBase) * 100 : 0;
      return {
        label: bt,
        color: LOB_PALETTE[i % LOB_PALETTE.length],
        value: arr,
        mom: arr - prevArr,
        newWon: pNew + pUp,
        churn: pChurn + pDn,
        nrr,
        hasNrr: prevArr > 0,
        share: tot > 0 ? (arr / tot) * 100 : 0,
      };
    });
  }, [ref.deals, ref.allRangeMonths, ref.latest, ref.prev]);

  const customerRows = useMemo(() => {
    const q = search.toLowerCase();
    const rows = buildCustomerRows(ref.deals, ref.latest, ref.prev, ref.allRangeMonths)
      .filter((row) => !q || [row.customer, row.region, row.sales_person, row.sub_product].some((v) => String(v || '').toLowerCase().includes(q)));
    return [...rows].sort((a, b) => {
      const av = a[sortField];
      const bv = b[sortField];
      if (typeof av === 'string') return sortDir * String(bv || '').localeCompare(String(av || ''));
      return sortDir * (Number(bv || 0) - Number(av || 0));
    });
  }, [ref, search, sortField, sortDir]);

  const quarterData = useMemo(() => {
    const quarterLabel = (month) => `Q${Math.ceil(Number(month.slice(5, 7)) / 3)} ${month.slice(0, 4)}`;
    // Sort key independent of object insertion order, and matching the
    // reference's own year-then-quarter comparator exactly.
    const quarterKey = (label) => {
      const [q, year] = label.split(' ');
      return Number(year) * 4 + Number(q.slice(1));
    };
    const sortAndTrim = (map) => Object.entries(map)
      .map(([label, value]) => ({ label, value }))
      .sort((a, b) => quarterKey(a.label) - quarterKey(b.label))
      .slice(-12);

    // New+Upsell per month across the full period filter (not the 18-month-
    // capped ref.waterfall, which would silently drop earlier quarters for
    // any period longer than ~1.5 years, e.g. LTM or a wide custom range).
    const newTotals = {};
    ref.allRangeMonths.forEach((month) => {
      let monthTotal = 0;
      ref.deals.forEach((deal) => {
        const c = changeForDeal(deal, month);
        monthTotal += Math.max(0, Number(c.new || 0)) + Math.max(0, Number(c.upsell || 0));
      });
      if (!monthTotal) return;
      const q = quarterLabel(month);
      newTotals[q] = (newTotals[q] || 0) + monthTotal;
    });

    // Closing ARR per quarter = ARR at that quarter's LAST month (end-of-
    // quarter snapshot), matching the reference's "keep overwriting" logic —
    // NOT a sum across the quarter's months.
    const arrTotals = {};
    ref.playTrend.forEach((point) => {
      if (point.month < ref.from || point.month > ref.to) return;
      const q = quarterLabel(point.month);
      arrTotals[q] = Number(point.value || 0);
    });

    return {
      new: sortAndTrim(newTotals),
      arr: sortAndTrim(arrTotals),
    };
  }, [ref.deals, ref.allRangeMonths, ref.playTrend, ref.from, ref.to]);

  const subProducts = useMemo(() => [''].concat(productTrendGroups.map((product) => product.label)), [productTrendGroups]);
  const selectedSubTrend = useMemo(() => {
    const deals = subSelect ? ref.deals.filter((deal) => (deal[productTrendField] || 'Unspecified') === subSelect) : ref.deals;
    // ref.allRangeMonths (not ref.months) — matches the reference's own
    // getMonths(f.from,f.to), i.e. the live period filter's month range, and
    // must match whatever list the chart's X-axis is drawn from below.
    return trendByField(deals, ref.allRangeMonths, productTrendField, 'arr');
  }, [ref, subSelect, productTrendField]);

  const lobTrend = useMemo(() => {
    // ref.allRangeMonths (not ref.months) tracks the live period filter — see
    // the same fix applied to the Product trend chart. Sorted
    // alphabetically so color index assignment matches the LOB cards above
    // (LOB_PALETTE is assigned by alphabetical index, e.g. Commercial=blue),
    // and each label gets the reference's own " Closing"/" New+Up" suffix.
    const raw = trendByField(ref.deals, ref.allRangeMonths, 'booking_type', lobTrendMode);
    const suffix = lobTrendMode === 'arr' ? ' Closing' : ' New+Up';
    return [...raw]
      .sort((a, b) => a.label.localeCompare(b.label))
      .map((seriesItem) => ({ ...seriesItem, label: `${seriesItem.label}${suffix}` }));
  }, [ref.deals, ref.allRangeMonths, lobTrendMode]);

  const buItems = (buMode === 'arr' ? groups.bu.map((row) => ({ label: row.label, value: row.value })) : groups.bu.map((row) => ({ label: row.label, value: row.newWon })));
  // Matches the reference's buildSP exactly: deals with no sales_person are
  // excluded entirely (not bucketed as "Unspecified"), and each toggle mode
  // is independently sorted/sliced to its own top 12 by its own metric —
  // reusing the Closing-ARR-sorted order for New Wins would both rank reps
  // wrong and leave zero-activity reps in the chart.
  const repItems = groups.reps
    .filter((row) => row.label !== 'Unspecified' && Number(repMode === 'arr' ? row.value : row.newWon) > 0)
    .map((row) => ({ label: row.label, value: repMode === 'arr' ? row.value : row.newWon }))
    .sort((a, b) => b.value - a.value)
    .slice(0, 12);

  const toggleSort = (field) => {
    if (sortField === field) setSortDir((d) => -d);
    else {
      setSortField(field);
      setSortDir(-1);
    }
  };

  const Th = ({ field, label, right }) => (
    <th onClick={() => toggleSort(field)} style={{ textAlign: right ? 'right' : 'left' }}>
      {label} <span style={{ fontSize: 9, color: '#94a3b8' }}>{sortField === field ? (sortDir < 0 ? 'v' : '^') : '-'}</span>
    </th>
  );

  // Separate sort state from the Customer table above — matches the
  // reference's own independent subTblSort, defaulting to Current ARR desc.
  const toggleSubTblSort = (field) => {
    if (subTblSortKey === field) setSubTblSortDir((d) => -d);
    else {
      setSubTblSortKey(field);
      setSubTblSortDir(-1);
    }
  };

  const SubTh = ({ field, label, right }) => (
    <th onClick={() => toggleSubTblSort(field)} style={{ textAlign: right ? 'right' : 'left', cursor: 'pointer' }}>
      {label} <span style={{ fontSize: 9, color: '#94a3b8' }}>{subTblSortKey === field ? (subTblSortDir < 0 ? 'v' : '^') : '-'}</span>
    </th>
  );

  const subTableRows = useMemo(() => {
    // "Other" matches the reference's exact fallback label for deals with no
    // sub_product (this table includes them, unlike the donut which excludes
    // them entirely — the two cards intentionally differ here in html).
    const withDisplay = productTrendGroups.map((product) => ({
      ...product,
      displayLabel: product.label === 'Unspecified' ? 'Other' : product.label,
      churnAbs: Math.abs(Number(product.churn || 0)),
    }));
    const keyFor = {
      label: (row) => row.displayLabel.toLowerCase(),
      value: (row) => Number(row.value || 0),
      newWon: (row) => Number(row.newWon || 0),
      churnAbs: (row) => Number(row.churnAbs || 0),
    }[subTblSortKey] || ((row) => Number(row.value || 0));
    return [...withDisplay].sort((a, b) => {
      const av = keyFor(a);
      const bv = keyFor(b);
      if (typeof av === 'string') return subTblSortDir < 0 ? bv.localeCompare(av) : av.localeCompare(bv);
      return subTblSortDir < 0 ? bv - av : av - bv;
    });
  }, [productTrendGroups, subTblSortKey, subTblSortDir]);

  const kpiForSankey = {
    ltm_opening_arr: ref.sankeyOpeningARR,
    total_arr: ref.curr,
    ltm_new_arr: ref.sankeyNew,
    ltm_upsell: ref.sankeyUp,
    ltm_renewal: ref.sankeyRen,
    ltm_churn: ref.sankeyChurn,
    ltm_downsell: ref.sankeyDn,
  };

  return (
    <>
      <div className="arr-kpi-strip">
        <DevOverlay name="KPI: Total ARR">
          <KpiCard label="Total ARR" value={fmt(ref.curr)} sub="Closing as of today" variant={1} detail={details.total} icon="💰"
          />
        </DevOverlay>
        <DevOverlay name={`KPI: ${ref.periodLabel} Change`}>
          <KpiCard label={`${ref.periodLabel} Change`} value={`${ref.periodChange >= 0 ? '+' : ''}${fmt(ref.periodChange)}`} delta={`${ref.periodChangePct >= 0 ? '+' : ''}${ref.periodChangePct.toFixed(1)}%`} sub="vs period start" variant={2} detail={details.change} icon="📈"
          />
        </DevOverlay>
        <DevOverlay name={`${ref.periodLabel} Growth`}>
          <MetricCard label={`${ref.periodLabel} Growth`} value={`${ref.periodChangePct >= 0 ? '+' : ''}${ref.periodChangePct.toFixed(1)}%`} sub={`vs period start | Won: ${fmt(ref.pNew + ref.pUp)} | Shrinkage: ${fmt(Math.abs(ref.pChurn) + ref.pDn)}`} icon="📈" color={ref.periodChangePct >= 0 ? '#15803d' : '#b91c1c'} detail={details.growth} />
        </DevOverlay>
      </div>

      <div className="arr-kpi-strip">
        <DevOverlay name={`KPI: ${ref.periodLabel} Bookings`}>
          <KpiCard label="Bookings (New + Expansion)" value={fmt(ref.pNew + ref.pUp)} sub="Total new revenue won this period" variant={1} detail={details.bookings} icon="💼"
          />
        </DevOverlay>
        <DevOverlay name={`KPI: ${ref.periodLabel} New ARR`}>
          <KpiCard label="New Booking" value={fmt(ref.pNew)} sub="New logos & first-time bookings" variant={3} detail={details.new} icon="🆕"
          />
        </DevOverlay>
        <DevOverlay name={`KPI: ${ref.periodLabel} Upsell`}>
          <KpiCard label="Expansion Bookings" value={fmt(ref.pUp)} sub="Expansion from existing customers" variant={4} detail={details.upsell} icon="⬆"
          />
        </DevOverlay>
      </div>

      <div className="arr-kpi-strip">
        <DevOverlay name={`KPI: ${ref.periodLabel} Contractions`}>
          <KpiCard label="Contractions (Churn + Downsell)" value={fmt(Math.abs(ref.pChurn) + ref.pDn)} sub="Total ARR shrinkage this period" variant={5} detail={details.contractions} icon="📉"
          />
        </DevOverlay>
        <DevOverlay name={`KPI: ${ref.periodLabel} Churn`}>
          <KpiCard label="Churn" value={fmt(Math.abs(ref.pChurn))} sub="Shrinkage ARR from cancellations" variant={5} detail={details.churn} icon="🔴"
          />
        </DevOverlay>
        <DevOverlay name={`KPI: ${ref.periodLabel} Downsell`}>
          <KpiCard label="Downsell" value={fmt(ref.pDn)} sub="Contraction from existing customers" variant={6} detail={details.downsell} icon="🔙"
          />
        </DevOverlay>
      </div>

      <div className="arr-2col arr-reference-gauges" style={{ gridTemplateColumns: '1fr 1fr' }}>
        <DevOverlay name="GRR Gauge Chart">
          <div
            className={`arr-chart-card arr-gauge-card arr-gauge-grr${details.grr ? ' is-clickable' : ''}`}
            onClick={() => details.grr && setGrrGaugeOpen(true)}
            role={details.grr ? 'button' : undefined}
            tabIndex={details.grr ? 0 : undefined}
            onKeyDown={(event) => {
              if (details.grr && (event.key === 'Enter' || event.key === ' ')) {
                event.preventDefault();
                setGrrGaugeOpen(true);
              }
            }}
          >
            {details.grr && <div className="arr-metric-click">Click for details</div>}
            <div className="arr-chart-title">GRR% - Gross Revenue Retention</div>
            <GaugeChart value={ref.grr} label="Retention excl. expansion · Churn+DS impact" color="#7c3aed" maxVal={150} />
            {grrGaugeOpen && <ArrDetailModal detail={details.grr} onClose={() => setGrrGaugeOpen(false)} />}
          </div>
        </DevOverlay>
        <DevOverlay name="NRR Gauge Chart">
          <div
            className={`arr-chart-card arr-gauge-card arr-gauge-nrr${details.nrr ? ' is-clickable' : ''}`}
            onClick={() => details.nrr && setNrrGaugeOpen(true)}
            role={details.nrr ? 'button' : undefined}
            tabIndex={details.nrr ? 0 : undefined}
            onKeyDown={(event) => {
              if (details.nrr && (event.key === 'Enter' || event.key === ' ')) {
                event.preventDefault();
                setNrrGaugeOpen(true);
              }
            }}
          >
            {details.nrr && <div className="arr-metric-click">Click for details</div>}
            <div className="arr-chart-title">NRR% - Net Revenue Retention</div>
            <GaugeChart value={ref.nrr} label="Incl. expansion · >100% = existing base growing" color="#0891b2" maxVal={150} />
            {nrrGaugeOpen && <ArrDetailModal detail={details.nrr} onClose={() => setNrrGaugeOpen(false)} />}
          </div>
        </DevOverlay>
      </div>

      <DevOverlay name="ARR Treemap">
        <div className="arr-chart-card" style={{ marginBottom: 18 }}>
          <div className="arr-chart-hdr">
            <div>
              <div className="arr-chart-title">ARR Treemap</div>
              <div className="arr-card-note">Customer ARR size from the same filtered deal set</div>
            </div>
            <span className="arr-sh-tag">{groups.customers.length} customers</span>
          </div>
          <Treemap items={groups.customers} height={300} onCellClick={onSelectCustomer} />
        </div>
      </DevOverlay>

      <DevOverlay name="ARR Flow Sankey">
        <div className="arr-chart-card" style={{ marginBottom: 18 }}>
          <div className="arr-chart-title" style={{ marginBottom: 2 }}>ARR Flow - Sankey Diagram</div>
          <div className="arr-card-note" style={{ marginBottom: 12 }}>How ARR flows: Opening -&gt; New / Expansion -&gt; Churn -&gt; Closing (current filter period)</div>
          <SankeyChart kpis={kpiForSankey} />
        </div>
      </DevOverlay>

      <DevOverlay name="ARR Trend Chart">
        <div className="arr-chart-card" style={{ marginBottom: 18 }}>
          <div className="arr-chart-title" style={{ marginBottom: 2 }}>ARR Trend - Monthly Closing ARR</div>
          <div className="arr-card-note" style={{ marginBottom: 12 }}>Total closing ARR month on month across all deals</div>
          <TrendPlaybackChart data={ref.playTrend} />
        </div>
      </DevOverlay>

      <DevOverlay name="ARR Waterfall">
        <div className="arr-chart-card" style={{ marginBottom: 18 }}>
          <div className="arr-chart-title" style={{ marginBottom: 2 }}>Changes in ARR - Waterfall by Month</div>
          <div className="arr-card-note" style={{ marginBottom: 12 }}>New, Expansion, Churn, Downsell (incl. FX) per month</div>
          <WaterfallChart data={ref.waterfall} />
        </div>
      </DevOverlay>

      <div className="arr-2col">
        <DevOverlay name="ARR by Business Unit">
          <div className="arr-chart-card">
            <div className="arr-chart-hdr">
              <div>
                <div className="arr-chart-title" style={{ marginBottom: 2 }}>ARR by BU</div>
                <div className="arr-card-note">Current closing ARR vs period New ARR</div>
              </div>
              <ChartToggle
                value={buMode}
                onChange={setBuMode}
                options={[
                  { label: 'Closing ARR', value: 'arr' },
                  { label: 'New Wins (Period)', value: 'new' },
                ]}
              />
            </div>
            <BUChart items={buItems} mode={buMode} />
          </div>
        </DevOverlay>
        <DevOverlay name="ARR by Sales Rep">
          <div className="arr-chart-card">
            <div className="arr-chart-hdr">
              <div>
                <div className="arr-chart-title" style={{ marginBottom: 2 }}>ARR by Sales Rep</div>
                <div className="arr-card-note">Current closing ARR vs period New ARR</div>
              </div>
              <ChartToggle
                value={repMode}
                onChange={setRepMode}
                options={[
                  { label: 'Closing ARR', value: 'arr' },
                  { label: 'New Wins (Period)', value: 'new' },
                ]}
              />
            </div>
            <BarChart
              items={repItems}
              maxItems={12}
              formatValue={repMode === 'arr' ? fmtHtmlMillions : fmtHtmlCompact}
              onItemClick={onSelectRep ? (item) => onSelectRep(item.label) : undefined}
            />
          </div>
        </DevOverlay>
      </div>

      <div className="arr-2col">
        <DevOverlay name="New ARR by Quarter">
          <div className="arr-chart-card">
            <div className="arr-chart-hdr">
              <div>
                <div className="arr-chart-title" style={{ marginBottom: 2 }}>New ARR by Quarter</div>
                <div className="arr-card-note">New bookings per quarter - closing ARR vs new ARR</div>
              </div>
              <ChartToggle
                value={quarterMode}
                onChange={setQuarterMode}
                options={[
                  { label: 'New ARR', value: 'new' },
                  { label: 'Closing ARR', value: 'arr' },
                ]}
              />
            </div>
            <VerticalBarChart
              items={quarterData[quarterMode]}
              color={quarterMode === 'new' ? '#1e5fa8' : '#15803d'}
              formatValue={fmtHtmlCompact}
            />
          </div>
        </DevOverlay>
        <DevOverlay name="ARR by Product">
          <div className="arr-chart-card">
            <div className="arr-chart-hdr">
              <div>
                <div className="arr-chart-title" style={{ marginBottom: 2 }}>ARR by Product</div>
                <div className="arr-card-note" style={{ marginBottom: 14 }}>Current closing ARR split by {productDonutMode === 'category' ? 'product category' : 'product'}</div>
              </div>
              <ChartToggle
                value={productDonutMode}
                onChange={setProductDonutMode}
                options={[
                  { label: 'Product', value: 'product' },
                  { label: 'Product Category', value: 'category' },
                ]}
              />
            </div>
            <SubProductDonut items={productDonutGroups.filter((product) => product.label !== 'Unspecified').map((product) => ({ label: product.label, value: product.value }))} />
          </div>
        </DevOverlay>
      </div>

      <div className="arr-2col">
        <DevOverlay name="Churn & Downsell Trend">
          <div className="arr-chart-card">
            <div className="arr-chart-title" style={{ marginBottom: 2 }}>Churn & Downsell Trend</div>
            <div className="arr-card-note" style={{ marginBottom: 14 }}>Monthly contraction - Churn and Downsell (incl. FX)</div>
            <ContractionTrendChart data={ref.waterfall} />
          </div>
        </DevOverlay>
        <DevOverlay name="Product Trend Table">
          <div className="arr-chart-card">
            <div className="arr-chart-hdr">
              <div>
                <div className="arr-chart-title" style={{ marginBottom: 2 }}>Product ARR - Trend & Table</div>
                <div className="arr-card-note">Select {productTrendMode === 'category' ? 'a product category' : 'a product'} to drill in, or view all as table</div>
              </div>
              <div className="arr-sub-controls">
                <select className="arr-inline-select" value={subSelect} onChange={(event) => setSubSelect(event.target.value)}>
                  <option value="">{productTrendMode === 'category' ? 'All Product Categories' : 'All Products'}</option>
                  {subProducts.filter(Boolean).map((product) => <option key={product} value={product}>{product}</option>)}
                </select>
                <ChartToggle
                  value={productTrendMode}
                  onChange={(value) => { setProductTrendMode(value); setSubSelect(''); }}
                  options={[
                    { label: 'Product', value: 'product' },
                    { label: 'Product Category', value: 'category' },
                  ]}
                />
                <ChartToggle
                  value={subView}
                  onChange={setSubView}
                  options={[
                    { label: 'Chart', value: 'chart' },
                    { label: 'Table', value: 'table' },
                  ]}
                />
              </div>
            </div>
            {subView === 'chart' ? (
              <MultiTrendChart series={selectedSubTrend} months={ref.allRangeMonths} height={210} />
            ) : (
              <div className="arr-twrap" style={{ maxHeight: 220 }}>
                <table>
                  <thead>
                    <tr>
                      <SubTh field="label" label={productTrendMode === 'category' ? 'Product Category' : 'Product'} />
                      <SubTh field="value" label="Current ARR" right />
                      <SubTh field="newWon" label="New Won" right />
                      <SubTh field="churnAbs" label="Churn/Downsell" right />
                      <th style={{ textAlign: 'right' }}>Share</th>
                    </tr>
                  </thead>
                  <tbody>
                    {subTableRows.map((product) => (
                      <tr key={product.label}>
                        <td><strong>{product.displayLabel}</strong></td>
                        <td className="arr-td-money">{fmtFull(product.value)}</td>
                        <td className="arr-td-money arr-up">{moneyDash(product.newWon)}</td>
                        <td className="arr-td-money arr-dn">{moneyDash(product.churnAbs)}</td>
                        <td className="arr-td-right">{pct(product.value, ref.curr)}%</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </DevOverlay>
      </div>

      <SectionHeader title="ARR by LOB" tag={`Total ARR: ${fmt(ref.curr)} | ${ref.latest || 'latest'}`} />
      <div className="arr-lob-grid">
        {lobCards.map((lob) => (
          <DevOverlay key={lob.label} name={`LOB: ${lob.label}`}>
            <div className="arr-lob-card" style={{ '--lob-color': lob.color }}>
              <div className="arr-lob-top">
                <h4>{lob.label}</h4>
                <span>{lob.share.toFixed(1)}% of total</span>
              </div>
              <div className="arr-lob-value">{fmt(lob.value)}</div>
              <div className="arr-card-note">Closing ARR as of {ref.latest || 'latest month'}</div>
              <div className="arr-lob-stats">
                <div><small>MoM</small><strong className={lob.mom >= 0 ? 'arr-up' : 'arr-dn'}>{dSign(lob.mom)}</strong></div>
                <div><small>{ref.periodLabel || 'YTD'} New+Up</small><strong className="arr-up">{fmt(lob.newWon)}</strong></div>
                <div><small>{ref.periodLabel || 'YTD'} Churn+Dn</small><strong className="arr-dn">{fmt(lob.churn)}</strong></div>
                <div><small>NRR</small><strong className={lob.nrr >= 100 ? 'arr-up' : 'arr-dn'}>{lob.hasNrr ? `${lob.nrr.toFixed(1)}%` : '-'}</strong></div>
              </div>
            </div>
          </DevOverlay>
        ))}
      </div>

      <DevOverlay name="LOB ARR Trend">
        <div className="arr-chart-card" style={{ marginBottom: 16 }}>
          <div className="arr-chart-hdr">
            <div>
              <div className="arr-chart-title" style={{ marginBottom: 2 }}>LOB ARR - Trend</div>
              <div className="arr-card-note">LOB Breakdown month on month</div>
            </div>
            <ChartToggle
              value={lobTrendMode}
              onChange={setLobTrendMode}
              options={[
                { label: 'Closing ARR', value: 'arr' },
                { label: 'New Won', value: 'new' },
              ]}
            />
          </div>
          <MultiTrendChart
            series={lobTrend}
            months={ref.allRangeMonths}
            height={290}
            colors={LOB_PALETTE}
            rawMonthLabels
            fillArea={lobTrendMode === 'arr'}
          />
        </div>
      </DevOverlay>

      <SectionHeader className="arr-sh-cust-detail" title="Customer ARR Detail" tag={`${customerRows.length} rows | Latest: ${ref.latest || '-'}`} />
      <DevOverlay name="Customer ARR Table">
        <div className="arr-tcard arr-tcard-cust-detail">
          <div className="arr-tcard-hdr">
            <span className="arr-tcard-title">
              <span className="arr-tcard-title-icon">👥</span>
              All Customers
            </span>
            <div className="arr-search-wrap">
              <span className="arr-search-icon">🔍</span>
              <input className="arr-search" placeholder="Search customer..." value={search} onChange={(e) => setSearch(e.target.value)} />
            </div>
          </div>
          <div className="arr-twrap arr-twrap-fixed">
            <table>
              <thead>
                <tr>
                  <Th field="customer" label="Customer" />
                  <Th field="region" label="BU" />
                  <Th field="sub_product" label="Product" />
                  <Th field="sales_person" label="Sales Rep" />
                  <Th field="curr_arr" label="Current ARR" right />
                  <Th field="prev_arr" label="Prior Month" right />
                  <Th field="mom" label="MoM Change" right />
                  <Th field="new_won" label="New + Expansion" right />
                  <Th field="period_churn" label="Churn / Downsell" right />
                  <th>Trend</th>
                </tr>
              </thead>
              <tbody>
                {customerRows.slice(0, 100).flatMap((row) => {
                  const id = `${row.customer}-${row.sub_product}`;
                  const isOpen = expanded === id;
                  const rows = [
                    <tr key={`row-${id}`} onClick={() => setExpanded((current) => current === id ? null : id)} style={{ cursor: 'pointer' }}>
                      <td><span style={{ marginRight: 6, fontSize: 10, color: '#94a3b8' }}>{isOpen ? 'v' : '>'}</span><strong>{row.customer}</strong></td>
                      <td><span className="arr-tag">{row.region || '-'}</span></td>
                      <td style={{ fontSize: 11 }}>{row.sub_product || '-'}</td>
                      <td style={{ fontSize: 12 }}>{row.sales_person || '-'}</td>
                      <td className="arr-td-money">{row.curr_arr ? fmtFull(row.curr_arr) : '-'}</td>
                      <td className="arr-td-money">{row.prev_arr ? fmtFull(row.prev_arr) : '-'}</td>
                      <td className={`arr-td-money ${row.mom > 0 ? 'arr-up' : row.mom < 0 ? 'arr-dn' : ''}`}>{arrowFull(row.mom)}</td>
                      <td className="arr-td-money arr-up">{row.new_won ? fmtFull(row.new_won) : '-'}</td>
                      <td className="arr-td-money arr-dn">{row.period_churn ? fmtFull(Math.abs(row.period_churn)) : '-'}</td>
                      <td><SparkLine data={row.trend} color={row.mom >= 0 ? '#059669' : '#dc2626'} /></td>
                    </tr>,
                  ];
                  if (isOpen) {
                    const isGrowing = row.curr_arr > 0 && row.mom >= 0 && row.new_won > 0;
                    const isDeclining = row.curr_arr > 0 && row.mom < 0;
                    const health = isGrowing ? 'growing' : isDeclining ? 'declining' : 'stable';
                    const healthLabel = isGrowing ? '▲ Growing' : isDeclining ? '▼ Declining' : '━ Stable';
                    rows.push(
                      <tr key={`exp-${id}`} className="arr-expand-row">
                        <td colSpan={10}>
                          <div className="arr-customer-expand">
                            <div><div className="arr-cust-detail-label">Closing ARR</div><div className="arr-cust-detail-val">{moneyDash(row.curr_arr)}</div></div>
                            <div><div className="arr-cust-detail-label">Prior Month</div><div className="arr-cust-detail-val" style={{ color: '#718096' }}>{moneyDash(row.prev_arr)}</div></div>
                            <div><div className="arr-cust-detail-label">MoM Change</div><div className={`arr-cust-detail-val ${row.mom > 0 ? 'arr-up' : row.mom < 0 ? 'arr-dn' : ''}`}>{signFull(row.mom)}</div></div>
                            <div><div className="arr-cust-detail-label">New+Expansion Won</div><div className="arr-cust-detail-val arr-up">{moneyDash(row.new_won)}</div></div>
                          </div>
                          <div className="arr-cust-tag">
                            👤 <strong>{row.customer}</strong>&nbsp;&nbsp;|&nbsp;&nbsp;{row.sub_product || '-'}&nbsp;&nbsp;|&nbsp;&nbsp;Rep: {row.sales_person || '-'}&nbsp;&nbsp;|&nbsp;&nbsp;{row.region || '-'}&nbsp;&nbsp;
                            <span className={`arr-cust-health arr-cust-health-${health}`}>{healthLabel}</span>
                          </div>
                        </td>
                      </tr>
                    );
                  }
                  return rows;
                })}
              </tbody>
            </table>
          </div>
        </div>
      </DevOverlay>

      <div className="arr-lob-drill-bar">
        <div className="arr-lob-drill-title">Drill Down by LOB</div>
        <select value={lobDrill} onChange={(event) => setLobDrill(event.target.value)}>
          <option value="">All ({groups.lob.map((lob) => lob.label).join(' + ')})</option>
          {groups.lob.map((lob) => <option key={lob.label} value={lob.label}>{lob.label}</option>)}
        </select>
        <div className="arr-lob-drill-tabs">
          <button type="button" className={lobDrillView === 'customer' ? 'on' : ''} onClick={() => setLobDrillView('customer')}>Customers</button>
          <button type="button" className={lobDrillView === 'salesrep' ? 'on' : ''} onClick={() => setLobDrillView('salesrep')}>Sales Reps</button>
          <button type="button" className={lobDrillView === 'subprod' ? 'on' : ''} onClick={() => setLobDrillView('subprod')}>Products</button>
        </div>
      </div>
      <DevOverlay name={`LOB Drill: ${lobDrill || 'All'}`}>
        <LOBDrillPanel refData={ref} lob={lobDrill} view={lobDrillView} setView={setLobDrillView} />
      </DevOverlay>
    </>
  );
}

export default ArrDashTab;
export { ArrDashTab };
